import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { extractBrandName, getWorkspaceBrandName } from '../../lib/brandName';
import { decodeHtmlEntities } from '../../lib/htmlEntities';
import { mergeResponseLinks, parseProviderLinks } from '../../lib/providerLinks';
import { InfoTooltip } from '../../components/InfoTooltip';
import { SearchResponseModal, type SearchResponseModalItem } from '../../components/SearchResponseModal';
import { useAuthStore } from '../../stores/authStore';
import type {
  Competitor,
  PromptResponse,
  PromptResponseCitation,
  PromptResponseMention,
  PromptRun,
  TrackedPrompt,
} from '../../types/database';

type ResponseRow = Pick<
  PromptResponse,
  | 'id'
  | 'created_at'
  | 'run_id'
  | 'model'
  | 'prompt_id'
  | 'prompt_text_snapshot'
  | 'response_text'
  | 'brand_mentioned'
  | 'brand_position'
  | 'competitors_mentioned'
  | 'citations'
  | 'provider_links'
>;

type PromptRow = Pick<TrackedPrompt, 'id' | 'text'>;
type PromptRunRow = Pick<PromptRun, 'id' | 'status' | 'completed_at' | 'created_at'>;

type PortalTooltipPosition = {
  left: number;
  top: number;
  placement: 'top' | 'bottom';
};

function MentionsHeaderPortalTooltip({ text }: { text: string }) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [position, setPosition] = useState<PortalTooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportPadding = 8;
    const tooltipGap = 10;
    const preferredTop = triggerRect.top - tooltipRect.height - tooltipGap;
    const shouldPlaceBelow = preferredTop < viewportPadding;
    const placement = shouldPlaceBelow ? 'bottom' : 'top';
    const top = shouldPlaceBelow
      ? triggerRect.bottom + tooltipGap
      : triggerRect.top - tooltipRect.height - tooltipGap;
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
    const left = Math.min(Math.max(centeredLeft, viewportPadding), Math.max(viewportPadding, maxLeft));

    setPosition({
      left,
      top: Math.max(viewportPadding, top),
      placement,
    });
  }, []);

  useEffect(() => {
    if (!isActive) {
      setPosition(null);
      return;
    }

    const frame = window.requestAnimationFrame(updatePosition);
    const handleViewportChange = () => updatePosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isActive, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="mentions-table-head-tooltip-trigger"
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        tabIndex={0}
        role="button"
        aria-label={text}
      >
        <span className="embed-icon is-tooltip">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </span>
      </span>
      {isActive && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={tooltipRef}
              className={`overview-portal-tooltip mentions-portal-tooltip is-${position?.placement || 'top'} ${position ? 'is-visible' : ''}`}
              style={{
                left: position?.left ?? -9999,
                top: position?.top ?? -9999,
              }}
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}

function MentionsPortalTextTooltip({
  text,
  children,
  triggerClassName,
  tooltipClassName,
  preferredPlacement = 'top',
  horizontalAlign = 'center',
}: {
  text: string;
  children: ReactNode;
  triggerClassName?: string;
  tooltipClassName?: string;
  preferredPlacement?: 'top' | 'bottom';
  horizontalAlign?: 'left' | 'center';
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [position, setPosition] = useState<PortalTooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportPadding = 8;
    const tooltipGap = 10;
    const preferredTop =
      preferredPlacement === 'bottom'
        ? triggerRect.bottom + tooltipGap
        : triggerRect.top - tooltipRect.height - tooltipGap;
    const shouldFlip =
      preferredPlacement === 'bottom'
        ? preferredTop + tooltipRect.height > window.innerHeight - viewportPadding
        : preferredTop < viewportPadding;
    const placement = shouldFlip
      ? preferredPlacement === 'bottom'
        ? 'top'
        : 'bottom'
      : preferredPlacement;
    const top =
      placement === 'bottom'
        ? triggerRect.bottom + tooltipGap
        : triggerRect.top - tooltipRect.height - tooltipGap;
    const targetLeft =
      horizontalAlign === 'left'
        ? triggerRect.left
        : triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
    const left = Math.min(Math.max(targetLeft, viewportPadding), Math.max(viewportPadding, maxLeft));

    setPosition({
      left,
      top: Math.max(viewportPadding, top),
      placement,
    });
  }, [horizontalAlign, preferredPlacement]);

  useEffect(() => {
    if (!isActive) {
      setPosition(null);
      return;
    }

    const frame = window.requestAnimationFrame(updatePosition);
    const handleViewportChange = () => updatePosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isActive, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className={triggerClassName}
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        tabIndex={0}
      >
        {children}
      </span>
      {isActive && typeof document !== 'undefined'
        ? createPortal(
            <span
              ref={tooltipRef}
              className={`overview-portal-tooltip mentions-portal-tooltip ${tooltipClassName || ''} is-${position?.placement || preferredPlacement} ${position ? 'is-visible' : ''}`}
              style={{
                left: position?.left ?? -9999,
                top: position?.top ?? -9999,
              }}
            >
              {text}
            </span>,
            document.body
          )
        : null}
    </>
  );
}

type StructuredMentionRow = Pick<
  PromptResponseMention,
  | 'prompt_response_id'
  | 'prompt_id'
  | 'run_id'
  | 'model'
  | 'brand_name'
  | 'brand_domain'
  | 'is_workspace_brand'
  | 'mentioned'
  | 'position'
  | 'mention_count'
  | 'created_at'
>;

type StructuredCitationRow = Pick<
  PromptResponseCitation,
  | 'prompt_response_id'
  | 'prompt_id'
  | 'run_id'
  | 'model'
  | 'citation_url'
  | 'citation_domain'
  | 'citation_title'
  | 'source_type'
  | 'source_category'
  | 'citation_position'
  | 'associated_brand_domains'
  | 'associated_brand_names'
  | 'context_snippet'
  | 'created_at'
>;

type CompetitorMentionEntry = {
  name?: unknown;
  domain?: unknown;
  mentioned?: unknown;
};

type BrandEntity = {
  name: string;
  domain: string;
  faviconUrl: string;
  isWorkspaceBrand: boolean;
};

type ResponseInsight = {
  id: string;
  createdAt: string;
  model: string;
  promptId: string | null;
  promptText: string;
  responseText: string;
  citations: string[];
  mentionedBrandDomains: string[];
  positionsByDomain: Map<string, number>;
};

type BrandRow = {
  name: string;
  domain: string;
  faviconUrl: string;
  mentions: number;
  promptCount: number;
  avgPosition: number | null;
  citedLinks: number;
};

type MentionDetail = {
  responseId: string;
  createdAt: string;
  model: string;
  promptText: string;
  responseText: string;
  snippet?: string;
};

type MentionLinkRow = {
  url: string;
  domain: string;
  title: string;
  faviconUrl: string;
  mentionCategory: 'Direct' | 'Indirect';
  sourceType: 'Direct Brand' | 'Social' | 'Government' | 'News' | 'Tech';
  count: number;
  promptText: string;
  brandDomains: string[];
  brandNames: string[];
  details: MentionDetail[];
};

type PromptMentionRow = {
  rowKey: string;
  promptId: string | null;
  promptText: string;
  model: string;
  mentionCategory: 'Direct' | 'Indirect';
  mentionCount: number;
  brandDomains: string[];
  brandNames: string[];
  responseBrandDomains: string[];
  responseBrandNames: string[];
  lastSeen: string;
  modalItem: SearchResponseModalItem | null;
};

type DonutDatum = {
  label: string;
  value: number;
  color: string;
  description?: string;
};

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArcPath = (
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};


const SOURCE_CATEGORY_CONFIG = [
  {
    label: 'Direct Brand',
    color: '#13c7bb',
    description: 'Citations from a tracked brand’s own website.',
  },
  {
    label: 'Social',
    color: '#e41a62',
    description: 'Citations from social platforms like LinkedIn, Reddit, X, or YouTube.',
  },
  {
    label: 'Government',
    color: '#ffcb10',
    description: 'Citations from government or public-sector domains.',
  },
  {
    label: 'News',
    color: '#e14dff',
    description: 'Citations from mainstream news and general editorial publishers.',
  },
  {
    label: 'Tech',
    color: '#1078ff',
    description: 'Citations from technology, software, industry-specific, and other third-party publishers.',
  },
] as const;

type PresetKey = 'today' | 'last7' | 'last30' | 'last90' | 'last365' | 'custom' | 'all';

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 3 months' },
  { key: 'last365', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom' },
  { key: 'all', label: 'All time' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function isSameDay(a: Date | null, b: Date | null) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isBetween(date: Date, start: Date | null, end: Date | null) {
  if (!start || !end) return false;
  const t = date.getTime();
  return t > start.getTime() && t < end.getTime();
}

function formatDateShort(date: Date) {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;
}

function Calendar({
  year,
  month,
  rangeStart,
  rangeEnd,
  onDayClick,
  onPrevMonth,
  onNextMonth,
  showPrevNav,
  showNextNav,
}: {
  year: number;
  month: number;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDayClick: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  showPrevNav: boolean;
  showNextNav: boolean;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date();

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

  const cells: { day: number; isOtherMonth: boolean; date: Date }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    cells.push({ day: d, isOtherMonth: true, date: new Date(prevYear, prevMonth, d) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, isOtherMonth: false, date: new Date(year, month, d) });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, isOtherMonth: true, date: new Date(nextYear, nextMonth, d) });
    }
  }

  return (
    <div className="date-range-calendar">
      <div className="calendar-header">
        <button
          className={`calendar-nav-btn ${!showPrevNav ? 'is-hidden' : ''}`}
          onClick={onPrevMonth}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <div className="calendar-month-title">
          {MONTH_NAMES[month]} {year}
        </div>
        <button
          className={`calendar-nav-btn ${!showNextNav ? 'is-hidden' : ''}`}
          onClick={onNextMonth}
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      </div>
      <div className="calendar-grid">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="calendar-day-header">{d}</div>
        ))}
        {cells.map((cell, i) => {
          const isSelected = isSameDay(cell.date, rangeStart) || isSameDay(cell.date, rangeEnd);
          const isInRange = isBetween(cell.date, rangeStart, rangeEnd);
          const isToday = isSameDay(cell.date, today) && !cell.isOtherMonth;
          const isFuture = cell.date > today;

          let cls = 'calendar-day';
          if (cell.isOtherMonth) cls += ' is-other-month';
          if (isFuture) cls += ' is-disabled';
          if (isSelected) cls += ' is-selected';
          if (isInRange && !cell.isOtherMonth) cls += ' is-in-range';
          if (isSelected && isSameDay(cell.date, rangeStart) && rangeEnd) cls += ' is-range-start';
          if (isSelected && isSameDay(cell.date, rangeEnd) && rangeStart) cls += ' is-range-end';
          if (isToday) cls += ' is-today';

          return (
            <button
              key={i}
              className={cls}
              onClick={() => !isFuture && !cell.isOtherMonth && onDayClick(cell.date)}
              type="button"
              disabled={isFuture}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const getValidatedAssociatedBrandDomains = (
  citation: StructuredCitationRow,
  trackedBrands: Array<{ domain: string; name: string }>
): string[] => {
  const trackedBrandDomains = new Set(trackedBrands.map((brand) => normalizeDomain(brand.domain)));
  return toStringArray(citation.associated_brand_domains)
    .map(normalizeDomain)
    .filter((domain) => trackedBrandDomains.has(domain));
};

const isWithinDateRange = (
  value: string,
  rangeStart: Date | null,
  rangeEnd: Date | null
): boolean => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;

  if (rangeStart) {
    const start = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
    if (timestamp < start) return false;
  }

  if (rangeEnd) {
    const end = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59, 999).getTime();
    if (timestamp > end) return false;
  }

  return true;
};

const getPromptModelKey = (item: { prompt_id: string | null; model: string; id: string }) =>
  `${item.prompt_id ?? `deleted:${item.id}`}:${item.model}`;

const formatModelLabel = (model: string): string => {
  const trimmed = model.trim();
  if (!trimmed) return 'Unknown';
  const normalized = trimmed.toLowerCase();
  if (normalized === 'chatgpt') return 'ChatGPT';
  if (normalized === 'perplexity') return 'Perplexity';
  if (normalized === 'claude') return 'Claude';
  if (normalized === 'gemini') return 'Gemini';
  return trimmed;
};

const buildEffectiveRangeData = (
  responseRows: ResponseRow[],
  mentionRows: StructuredMentionRow[],
  citationRows: StructuredCitationRow[]
) => {
  const latestResponsesByPromptModel = new Map<string, ResponseRow>();

  responseRows.forEach((response) => {
    const key = getPromptModelKey(response);
    const existing = latestResponsesByPromptModel.get(key);

    if (!existing || new Date(response.created_at).getTime() > new Date(existing.created_at).getTime()) {
      latestResponsesByPromptModel.set(key, response);
    }
  });

  const responses = Array.from(latestResponsesByPromptModel.values());
  const responseIds = new Set(responses.map((response) => response.id));

  return {
    responses,
    mentions: mentionRows.filter((mention) => responseIds.has(mention.prompt_response_id)),
    citations: citationRows.filter((citation) => responseIds.has(citation.prompt_response_id)),
  };
};

const cleanCitationUrl = (value: string): string =>
  value.trim().replace(/[),.;]+$/, '');

const parseCitations = (rawCitations: unknown, responseText: string): string[] => {
  const results: string[] = [];

  if (Array.isArray(rawCitations)) {
    for (const item of rawCitations) {
      if (typeof item === 'string') {
        const cleaned = cleanCitationUrl(item);
        if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
          results.push(cleaned);
        }
      }
    }
  }

  if (results.length === 0 && responseText) {
    const urlRegex = /https?:\/\/[^\s)"'<>\]]+/g;
    const matched = responseText.match(urlRegex) || [];
    for (const item of matched) {
      const cleaned = cleanCitationUrl(item);
      if (cleaned) results.push(cleaned);
    }
  }

  return Array.from(new Set(results));
};

const domainMatches = (inputDomain: string, targetDomain: string): boolean => {
  const input = normalizeDomain(inputDomain);
  const target = normalizeDomain(targetDomain);
  return input === target || input.endsWith(`.${target}`);
};

const mapStoredSourceCategory = (
  sourceCategory: PromptResponseCitation['source_category'],
  sourceType: PromptResponseCitation['source_type']
): 'Direct Brand' | 'Social' | 'Government' | 'News' | 'Tech' => {
  if (sourceCategory === 'direct_brand') return 'Direct Brand';
  if (sourceCategory === 'social') return 'Social';
  if (sourceCategory === 'government') return 'Government';
  if (sourceCategory === 'news') return 'News';
  if (sourceCategory === 'tech') return 'Tech';
  if (sourceType === 'direct') return 'Direct Brand';
  if (sourceType === 'social') return 'Social';
  return 'Tech';
};

const detectMentionCategory = (
  citationDomain: string,
  brandDomains: string[]
): 'Direct' | 'Indirect' =>
  brandDomains.some((domain) => domainMatches(citationDomain, domain)) ? 'Direct' : 'Indirect';

const citationMatchesBrand = (
  citation: StructuredCitationRow,
  brandDomain: string,
  trackedBrands: Array<{ domain: string; name: string }>
): boolean => {
  const citationDomain = normalizeDomain(citation.citation_domain || citation.citation_url || '');
  if (citationDomain && domainMatches(citationDomain, brandDomain)) return true;

  return getValidatedAssociatedBrandDomains(citation, trackedBrands).some((domain) =>
    domainMatches(domain, brandDomain)
  );
};

const citationIsDirectForBrand = (citation: StructuredCitationRow, brandDomain: string): boolean =>
  domainMatches(citation.citation_domain || citation.citation_url || '', brandDomain);

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const getHistoricalPromptText = (
  promptId: string | null | undefined,
  promptTextSnapshot: string | null | undefined,
  promptById: Map<string, string>
) => {
  if (promptId) {
    const livePromptText = promptById.get(promptId);
    if (livePromptText) return livePromptText;
  }

  if (promptTextSnapshot?.trim()) return promptTextSnapshot;
  return 'Prompt unavailable';
};

const extractRankPosition = (responseText: string, tokens: string[]): number | null => {
  if (!responseText || tokens.length === 0) return null;

  const normalizedTokens = tokens
    .map((token) => token.trim().toLowerCase())
    .filter((token) => !!token);
  if (normalizedTokens.length === 0) return null;

  const lines = responseText.split('\n');
  for (const rawLine of lines) {
    const line = rawLine.toLowerCase();
    const tokenMatched = normalizedTokens.some((token) => line.includes(token));
    if (!tokenMatched) continue;

    const directNumberMatch = rawLine.match(/^\s*(\d{1,2})[\.\)\-:]\s+/);
    if (directNumberMatch) {
      return parseInt(directNumberMatch[1], 10);
    }

    const cleanedLine = rawLine.replace(/^[\s>*-]+/, '');
    const cleanedNumberMatch = cleanedLine.match(/^(\d{1,2})[\.\)\-:]\s+/);
    if (cleanedNumberMatch) {
      return parseInt(cleanedNumberMatch[1], 10);
    }
  }

  return null;
};

const getCitationDisplayTitle = (url: string, domain: string): string => {
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname
      .split('/')
      .filter(Boolean)
      .map((part) =>
        decodeURIComponent(part)
          .replace(/[-_]+/g, ' ')
          .replace(/\.[a-z0-9]+$/i, '')
          .trim()
      )
      .filter(Boolean);

    if (parts.length > 0) {
      const candidate = parts[parts.length - 1];
      return candidate
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  } catch {
    // Fall through to domain-derived title.
  }

  return extractBrandName(domain);
};

function DonutChart({
  title,
  totalLabel,
  data,
}: {
  title: string;
  totalLabel: string;
  data: DonutDatum[];
}) {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const visibleData = data.filter((item) => item.value > 0);
  const radius = 42;
  const strokeWidth = 9;
  const activeLabel = hoveredLabel && data.some((item) => item.label === hoveredLabel) ? hoveredLabel : null;

  const updateTooltipPosition = useCallback((event: ReactMouseEvent<SVGElement>) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const rect = wrap.getBoundingClientRect();
    setTooltipPosition({
      left: event.clientX - rect.left,
      top: event.clientY - rect.top,
    });
  }, []);

  let accumulated = 0;

  return (
    <div className="mentions-donut-card">
      <div className="dashboard-panel-title">{title}</div>
      <div className="mentions-donut-shell">
        <div ref={wrapRef} className="mentions-donut-wrap">
          <svg className="mentions-donut-chart" viewBox="0 0 120 120" role="img" aria-label={title}>
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke="var(--background)"
              strokeWidth={strokeWidth}
            />
            {total > 0 &&
              visibleData.map((item) => {
                const fraction = item.value / total;
                const startAngle = accumulated * 360;
                const endAngle = startAngle + fraction * 360;
                accumulated += fraction;

                if (fraction >= 0.9999) {
                  return (
                    <circle
                      key={item.label}
                      cx="60"
                      cy="60"
                      r={radius}
                      fill="none"
                      stroke={item.color}
                      strokeWidth={strokeWidth}
                      strokeLinecap="butt"
                      className={`mentions-donut-segment ${activeLabel && activeLabel !== item.label ? 'is-dimmed' : ''}`}
                      onMouseEnter={(event) => {
                        setHoveredLabel(item.label);
                        updateTooltipPosition(event);
                      }}
                      onMouseMove={updateTooltipPosition}
                      onMouseLeave={() => {
                        setHoveredLabel(null);
                        setTooltipPosition(null);
                      }}
                    />
                  );
                }

                return (
                  <path
                    key={item.label}
                    d={describeArcPath(60, 60, radius, startAngle, endAngle)}
                    fill="none"
                    stroke={item.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="butt"
                    className={`mentions-donut-segment ${activeLabel && activeLabel !== item.label ? 'is-dimmed' : ''}`}
                    onMouseEnter={(event: ReactMouseEvent<SVGPathElement>) => {
                      setHoveredLabel(item.label);
                      updateTooltipPosition(event);
                    }}
                    onMouseMove={updateTooltipPosition}
                    onMouseLeave={() => {
                      setHoveredLabel(null);
                      setTooltipPosition(null);
                    }}
                  />
                );
              })}
          </svg>
          <div className="mentions-donut-center">
            <div className="mentions-donut-total">{total.toLocaleString()}</div>
            <div className="text-size-small text-colour-alt-grey">{totalLabel}</div>
          </div>
          {activeLabel && (
            <div
              className="mentions-donut-tooltip is-following-pointer"
              style={{
                left: tooltipPosition?.left ?? undefined,
                top: tooltipPosition?.top ?? undefined,
              }}
            >
              <div className="text-size-xsmall text-weight-medium">
                {activeLabel}: {data.find((item) => item.label === activeLabel)?.value.toLocaleString() || 0}
              </div>
            </div>
          )}
        </div>
        <div className="mentions-donut-legend">
          {data.map((item) => (
            <div key={item.label} className={`mentions-donut-legend-item ${activeLabel && activeLabel !== item.label ? 'is-dimmed' : ''}`}>
              <div className="mentions-donut-legend-swatch" style={{ backgroundColor: item.color }} />
              {item.description ? (
                <div className="info-tooltip-wrapper mentions-donut-legend-label-wrap">
                  <div className="text-size-xsmall text-weight-medium text-colour-dark-brown">{item.label}</div>
                  <span className="info-tooltip">{item.description}</span>
                </div>
              ) : (
                <div className="text-size-xsmall text-weight-medium text-colour-dark-brown">{item.label}</div>
              )}
              <div className="text-size-xsmall text-colour-alt-grey">{item.value.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PerformanceGauge({
  title,
  percentage,
}: {
  title: string;
  percentage: number;
}) {
  const clamped = Math.max(0, Math.min(100, percentage));
  const radius = 58;
  const circumference = Math.PI * radius;
  const dashOffset = circumference - (circumference * clamped) / 100;

  return (
    <div className="mentions-performance-gauge">
      <div className="dashboard-panel-title mentions-performance-gauge-title">{title}</div>
      <div className="mentions-performance-gauge-visual">
        <svg viewBox="0 0 160 96" className="mentions-performance-gauge-svg" role="img" aria-label={`${Math.round(clamped)} percent`}>
          <defs>
            <linearGradient id="mentions-performance-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff3b3b" />
              <stop offset="52%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#84cc16" />
            </linearGradient>
          </defs>
          <path
            d="M 22 80 A 58 58 0 0 1 138 80"
            className="mentions-performance-gauge-track"
            pathLength={circumference}
          />
          {clamped > 0 && (
            <path
              d="M 22 80 A 58 58 0 0 1 138 80"
              className="mentions-performance-gauge-fill"
              pathLength={circumference}
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: dashOffset,
              }}
            />
          )}
        </svg>
        <div className="mentions-performance-gauge-value">
          <div className="mentions-performance-gauge-number">{Math.round(clamped)}%</div>
          <div className="text-size-small text-colour-alt-grey">Visibility</div>
        </div>
      </div>
    </div>
  );
}

export function Mentions() {
  const { workspace } = useAuthStore();
  const [searchParams] = useSearchParams();
  const brandsParam = searchParams.get('brands');
  const brandParam = searchParams.get('brand') || searchParams.get('competitor');
  const promptFilter = searchParams.get('prompt');
  const initialBrandFilters = useMemo(() => {
    if (brandsParam) {
      return Array.from(
        new Set(
          brandsParam
            .split(',')
            .map((value) => normalizeDomain(value))
            .filter(Boolean)
        )
      );
    }

    if (brandParam) {
      return [normalizeDomain(brandParam)];
    }

    return [];
  }, [brandParam, brandsParam]);

  const [viewMode, setViewMode] = useState<'brands' | 'citations' | 'prompts'>('prompts');
  const [selectedPromptId, setSelectedPromptId] = useState(promptFilter || 'all');
  const [selectedBrandDomains, setSelectedBrandDomains] = useState<string[]>([]);
  const [gapOnly] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetKey>('today');
  const [appliedPreset, setAppliedPreset] = useState<PresetKey>('today');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [isPromptDropdownOpen, setIsPromptDropdownOpen] = useState(false);
  const [isTrackedBrandDropdownOpen, setIsTrackedBrandDropdownOpen] = useState(false);
  const [selectedPromptModalItem, setSelectedPromptModalItem] = useState<SearchResponseModalItem | null>(null);
  const [promptPage, setPromptPage] = useState(1);
  const [brandPage, setBrandPage] = useState(1);
  const [citationPage, setCitationPage] = useState(1);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const promptDropdownRef = useRef<HTMLDivElement>(null);
  const trackedBrandDropdownRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const [rangeStart, setRangeStart] = useState<Date | null>(
    new Date(today.getTime() - 24 * 60 * 60 * 1000)
  );
  const [rangeEnd, setRangeEnd] = useState<Date | null>(today);
  const [tempStart, setTempStart] = useState<Date | null>(rangeStart);
  const [tempEnd, setTempEnd] = useState<Date | null>(rangeEnd);
  const [pickingStart, setPickingStart] = useState(true);
  const [leftMonth, setLeftMonth] = useState(today.getMonth() === 0 ? 11 : today.getMonth() - 1);
  const [leftYear, setLeftYear] = useState(today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear());

  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    document.querySelectorAll('.section-application, .container-full').forEach((element) => {
      if (element instanceof HTMLElement) {
        element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
  }, [searchParams]);

  useEffect(() => {
    setSelectedPromptId(promptFilter || 'all');
  }, [promptFilter]);

  const openDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    setDatePickerVisible(true);
    requestAnimationFrame(() => {
      setIsDatePickerOpen(true);
    });
  }, [appliedPreset, rangeStart, rangeEnd]);

  const closeDatePicker = useCallback(() => {
    setIsDatePickerOpen(false);
    setTimeout(() => setDatePickerVisible(false), 120);
  }, []);

  const dismissDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    closeDatePicker();
  }, [appliedPreset, closeDatePicker, rangeEnd, rangeStart]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        dismissDatePicker();
      }
      if (promptDropdownRef.current && !promptDropdownRef.current.contains(e.target as Node)) {
        setIsPromptDropdownOpen(false);
      }
      if (trackedBrandDropdownRef.current && !trackedBrandDropdownRef.current.contains(e.target as Node)) {
        setIsTrackedBrandDropdownOpen(false);
      }
    };
    if (datePickerVisible || isPromptDropdownOpen || isTrackedBrandDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [datePickerVisible, dismissDatePicker, isPromptDropdownOpen, isTrackedBrandDropdownOpen]);

  const handlePresetClick = (key: PresetKey) => {
    setActivePreset(key);
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;

    if (key === 'today') start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    else if (key === 'last7') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    else if (key === 'last30') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 27);
    else if (key === 'last90') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    else if (key === 'last365') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    else if (key === 'all') { start = null; end = null; }
    else if (key === 'custom') { return; }

    setTempStart(start);
    setTempEnd(end);
    setRangeStart(start);
    setRangeEnd(end);
    setAppliedPreset(key);
    closeDatePicker();
  };

  const handleDayClick = (date: Date) => {
    setActivePreset('custom');
    if (pickingStart) {
      setTempStart(date);
      setTempEnd(null);
      setPickingStart(false);
    } else {
      if (tempStart && date < tempStart) {
        setTempEnd(tempStart);
        setTempStart(date);
      } else {
        setTempEnd(date);
      }
      setPickingStart(true);
    }
  };

  const handleApply = () => {
    setAppliedPreset(activePreset);
    setRangeStart(tempStart);
    setRangeEnd(tempEnd);
    closeDatePicker();
  };

  const handleCancel = () => dismissDatePicker();

  const handlePrevMonth = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear(leftYear - 1);
    } else {
      setLeftMonth(leftMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear(leftYear + 1);
    } else {
      setLeftMonth(leftMonth + 1);
    }
  };

  const dateLabel = (() => {
    const preset = PRESETS.find((p) => p.key === appliedPreset);
    if (appliedPreset !== 'custom') return preset?.label || 'Today';
    if (rangeStart && rangeEnd) return `${formatDateShort(rangeStart)} – ${formatDateShort(rangeEnd)}`;
    return 'Custom';
  })();

  const { data: responses = [] } = useQuery({
    queryKey: ['mentions_prompt_responses', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_responses')
        .select(
          'id, created_at, run_id, model, prompt_id, prompt_text_snapshot, response_text, brand_mentioned, brand_position, competitors_mentioned, citations, provider_links'
        )
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ResponseRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: prompts = [] } = useQuery({
    queryKey: ['mentions_tracked_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('id, text')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PromptRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: promptRuns = [] } = useQuery({
    queryKey: ['mentions_prompt_runs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_runs')
        .select('id, status, completed_at, created_at')
        .eq('workspace_id', workspace.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return data as PromptRunRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: structuredMentions = [] } = useQuery({
    queryKey: ['mentions_structured_mentions', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_response_mentions')
        .select(
          'prompt_response_id, prompt_id, run_id, model, brand_name, brand_domain, is_workspace_brand, mentioned, position, mention_count, created_at'
        )
        .eq('workspace_id', workspace.id)
        .eq('mentioned', true)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }
      return data as StructuredMentionRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: structuredCitations = [] } = useQuery({
    queryKey: ['mentions_structured_citations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const withTitleSelect =
        'prompt_response_id, prompt_id, run_id, model, citation_url, citation_domain, citation_title, source_type, source_category, citation_position, associated_brand_domains, associated_brand_names, context_snippet, created_at';
      const fallbackSelect =
        'prompt_response_id, prompt_id, run_id, model, citation_url, citation_domain, source_type, citation_position, associated_brand_domains, associated_brand_names, context_snippet, created_at';

      let { data, error } = await supabase
        .from('prompt_response_citations')
        .select(withTitleSelect)
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      const missingDerivedColumns =
        !!error &&
        (error.code === '42703' ||
          error.code === 'PGRST204' ||
          error.message?.toLowerCase().includes('citation_title') ||
          error.message?.toLowerCase().includes('source_category'));

      if (missingDerivedColumns) {
        const fallbackResult = await supabase
          .from('prompt_response_citations')
          .select(fallbackSelect)
          .eq('workspace_id', workspace.id)
          .order('created_at', { ascending: false });

        data = fallbackResult.data?.map((row) => ({
          ...row,
          citation_title: null,
          source_category: null,
        })) as StructuredCitationRow[] | null;
        error = fallbackResult.error;
      }

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }
      return (data || []) as StructuredCitationRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: competitors = [] } = useQuery({
    queryKey: ['mentions_competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('name, domain, favicon_url')
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      return data as Pick<Competitor, 'name' | 'domain' | 'favicon_url'>[];
    },
    enabled: !!workspace?.id,
  });

  const promptById = useMemo(() => {
    const map = new Map<string, string>();
    for (const prompt of prompts) {
      map.set(prompt.id, prompt.text);
    }
    return map;
  }, [prompts]);
  const trackedPromptIdSet = useMemo(() => new Set(prompts.map((prompt) => prompt.id)), [prompts]);

  const trackedBrandEntities = useMemo(() => {
    const entities: BrandEntity[] = [];
    const seenDomains = new Set<string>();
    const workspaceDomain = workspace?.brand_url ? normalizeDomain(workspace.brand_url) : '';

    if (workspace?.brand_url) {
      const brandDomain = workspaceDomain;
      if (brandDomain) {
        seenDomains.add(brandDomain);
        entities.push({
          name: getWorkspaceBrandName(workspace),
          domain: brandDomain,
          faviconUrl: `https://www.google.com/s2/favicons?domain=${brandDomain}&sz=64`,
          isWorkspaceBrand: true,
        });
      }
    }

    for (const competitor of competitors) {
      const domain = normalizeDomain(competitor.domain);
      if (!domain || seenDomains.has(domain)) continue;
      seenDomains.add(domain);
      entities.push({
        name: competitor.name || extractBrandName(domain),
        domain,
        faviconUrl:
          competitor.favicon_url ||
          `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
        isWorkspaceBrand: false,
      });
    }

    return entities;
  }, [workspace, workspace?.brand_url, competitors]);

  const brandEntities = useMemo(() => {
    const entities = [...trackedBrandEntities];
    const workspaceDomain = workspace?.brand_url ? normalizeDomain(workspace.brand_url) : '';
    const seenDomains = new Set(entities.map((entity) => entity.domain));

    for (const mention of structuredMentions) {
      const domain = normalizeDomain(mention.brand_domain);
      if (!domain || seenDomains.has(domain)) continue;
      seenDomains.add(domain);
      entities.push({
        name: mention.brand_name || extractBrandName(domain),
        domain,
        faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
        isWorkspaceBrand: workspaceDomain !== '' && domain === workspaceDomain,
      });
    }

    for (const citation of structuredCitations) {
      const associatedDomains = toStringArray(citation.associated_brand_domains).map(normalizeDomain);
      const associatedNames = toStringArray(citation.associated_brand_names);
      const validatedAssociatedDomains = getValidatedAssociatedBrandDomains(citation, entities);
      for (const domain of validatedAssociatedDomains) {
        if (!domain || seenDomains.has(domain)) continue;
        seenDomains.add(domain);
        const nameIndex = associatedDomains.findIndex((candidate) => domainMatches(candidate, domain));
        entities.push({
          name: associatedNames[nameIndex] || extractBrandName(domain),
          domain,
          faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
          isWorkspaceBrand: workspaceDomain !== '' && domain === workspaceDomain,
        });
      }
    }

    for (const response of responses) {
      if (workspaceDomain && response.brand_mentioned && !seenDomains.has(workspaceDomain)) {
        seenDomains.add(workspaceDomain);
        entities.push({
          name: getWorkspaceBrandName(workspace),
          domain: workspaceDomain,
          faviconUrl: `https://www.google.com/s2/favicons?domain=${workspaceDomain}&sz=64`,
          isWorkspaceBrand: true,
        });
      }

      if (!Array.isArray(response.competitors_mentioned)) continue;
      for (const item of response.competitors_mentioned as CompetitorMentionEntry[]) {
        if (!item || typeof item !== 'object' || item.mentioned !== true) continue;
        const domain = typeof item.domain === 'string' ? normalizeDomain(item.domain) : '';
        if (!domain || seenDomains.has(domain)) continue;
        seenDomains.add(domain);
        entities.push({
          name:
            typeof item.name === 'string' && item.name.trim()
              ? item.name.trim()
              : extractBrandName(domain),
          domain,
          faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
          isWorkspaceBrand: workspaceDomain !== '' && domain === workspaceDomain,
        });
      }
    }

    return entities;
  }, [workspace?.brand_url, responses, structuredCitations, structuredMentions, trackedBrandEntities]);

  const workspaceBrandDomain = useMemo(
    () => brandEntities.find((brand) => brand.isWorkspaceBrand)?.domain || null,
    [brandEntities]
  );

  const trackedFilterBrands = useMemo(
    () => trackedBrandEntities,
    [trackedBrandEntities]
  );

  useEffect(() => {
    const allDomains = brandEntities.map((brand) => brand.domain);
    if (allDomains.length === 0) {
      setSelectedBrandDomains((previous) => (previous.length === 0 ? previous : []));
      return;
    }

    setSelectedBrandDomains((previous) => {
      if (previous.length === 0) return previous;

      const filtered = previous.filter((domain) => allDomains.includes(domain));
      if (
        filtered.length === previous.length &&
        filtered.every((domain, index) => domain === previous[index])
      ) {
        return previous;
      }
      return filtered;
    });
  }, [brandEntities]);

  useEffect(() => {
    if (initialBrandFilters.length === 0 || brandEntities.length === 0) return;

    const availableDomains = new Set(brandEntities.map((brand) => brand.domain));
    const validFilters = initialBrandFilters.filter((domain) => availableDomains.has(domain));
    if (validFilters.length === 0) return;

    setSelectedBrandDomains((previous) => {
      if (
        previous.length === validFilters.length &&
        previous.every((domain, index) => domain === validFilters[index])
      ) {
        return previous;
      }

      return validFilters;
    });
  }, [brandEntities, initialBrandFilters]);

  const activeBrandDomainSet = useMemo(() => {
    if (selectedBrandDomains.length > 0) {
      return new Set<string>(selectedBrandDomains);
    }
    return new Set<string>(brandEntities.map((brand) => brand.domain));
  }, [selectedBrandDomains, brandEntities]);

  const trackedCompetitorDomainSet = useMemo(() => {
    const set = new Set<string>();
    for (const competitor of competitors) {
      const domain = normalizeDomain(competitor.domain);
      if (domain) set.add(domain);
    }
    return set;
  }, [competitors]);

  const activeGapCompetitorDomainSet = useMemo(() => {
    const set = new Set<string>();
    for (const domain of trackedCompetitorDomainSet) {
      if (activeBrandDomainSet.has(domain)) {
        set.add(domain);
      }
    }
    return set;
  }, [trackedCompetitorDomainSet, activeBrandDomainSet]);

  const filteredResponses = useMemo(() => {
    const latestCompletedRunId = promptRuns[0]?.id || null;
    return responses.filter((response) => {
      if (selectedPromptId !== 'all' && response.prompt_id !== selectedPromptId) return false;
      if (appliedPreset === 'today' && latestCompletedRunId) {
        return response.run_id === latestCompletedRunId;
      }
      return isWithinDateRange(response.created_at, rangeStart, rangeEnd);
    });
  }, [appliedPreset, promptRuns, responses, selectedPromptId, rangeStart, rangeEnd]);

  const filteredStructuredMentions = useMemo(() => {
    const latestCompletedRunId = promptRuns[0]?.id || null;
    return structuredMentions.filter((row) => {
      if (selectedPromptId !== 'all' && row.prompt_id !== selectedPromptId) return false;
      if (appliedPreset === 'today' && latestCompletedRunId) {
        return row.run_id === latestCompletedRunId;
      }
      return isWithinDateRange(row.created_at, rangeStart, rangeEnd);
    });
  }, [appliedPreset, promptRuns, structuredMentions, selectedPromptId, rangeStart, rangeEnd]);

  const filteredStructuredCitations = useMemo(() => {
    const latestCompletedRunId = promptRuns[0]?.id || null;
    return structuredCitations.filter((row) => {
      if (selectedPromptId !== 'all' && row.prompt_id !== selectedPromptId) return false;
      if (appliedPreset === 'today' && latestCompletedRunId) {
        return row.run_id === latestCompletedRunId;
      }
      return isWithinDateRange(row.created_at, rangeStart, rangeEnd);
    });
  }, [appliedPreset, promptRuns, structuredCitations, selectedPromptId, rangeStart, rangeEnd]);

  const effectiveFilteredRangeData = useMemo(
    () => buildEffectiveRangeData(filteredResponses, filteredStructuredMentions, filteredStructuredCitations),
    [filteredResponses, filteredStructuredMentions, filteredStructuredCitations]
  );

  const effectiveFilteredResponses = effectiveFilteredRangeData.responses;
  const effectiveFilteredStructuredMentions = effectiveFilteredRangeData.mentions;
  const effectiveFilteredStructuredCitations = effectiveFilteredRangeData.citations;

  const hasStructuredMentions = effectiveFilteredStructuredMentions.length > 0;
  const hasStructuredCitations = effectiveFilteredStructuredCitations.length > 0;

  const responseById = useMemo(() => {
    const map = new Map<string, ResponseRow>();
    for (const response of effectiveFilteredResponses) {
      map.set(response.id, response);
    }
    return map;
  }, [effectiveFilteredResponses]);

  const responseInsights = useMemo(() => {
    const byName = new Map<string, string>();
    for (const brand of brandEntities) {
      byName.set(brand.name.toLowerCase(), brand.domain);
    }

    return effectiveFilteredResponses.map((response) => {
      const lowerText = response.response_text.toLowerCase();
      const mentionedByArrayDomains = new Set<string>();
      const mentionedByArrayNames = new Set<string>();

      const rawMentions = response.competitors_mentioned;
      if (Array.isArray(rawMentions)) {
        for (const mention of rawMentions as CompetitorMentionEntry[]) {
          if (!mention || typeof mention !== 'object') continue;
          if (mention.mentioned !== true) continue;
          if (typeof mention.domain === 'string') {
            mentionedByArrayDomains.add(normalizeDomain(mention.domain));
          }
          if (typeof mention.name === 'string') {
            mentionedByArrayNames.add(mention.name.trim().toLowerCase());
          }
        }
      }

      const mentionedBrandDomains: string[] = [];
      const positionsByDomain = new Map<string, number>();
      for (const brand of brandEntities) {
        let mentioned = false;

        if (brand.isWorkspaceBrand && response.brand_mentioned === true) {
          mentioned = true;
        }
        if (!mentioned && mentionedByArrayDomains.has(brand.domain)) {
          mentioned = true;
        }
        if (!mentioned && mentionedByArrayNames.has(brand.name.toLowerCase())) {
          mentioned = true;
        }
        if (!mentioned) {
          mentioned =
            lowerText.includes(brand.domain.toLowerCase()) ||
            lowerText.includes(brand.name.toLowerCase());
        }

        if (!mentioned) continue;
        mentionedBrandDomains.push(brand.domain);

        const matchPosition =
          workspaceBrandDomain === brand.domain && typeof response.brand_position === 'number'
            ? response.brand_position
            : extractRankPosition(response.response_text, [brand.domain, brand.name]);

        if (typeof matchPosition === 'number' && Number.isFinite(matchPosition)) {
          positionsByDomain.set(brand.domain, matchPosition);
        }
      }

      for (const name of mentionedByArrayNames) {
        const matchedDomain = byName.get(name);
        if (matchedDomain && !mentionedBrandDomains.includes(matchedDomain)) {
          mentionedBrandDomains.push(matchedDomain);
        }
      }

      return {
        id: response.id,
        createdAt: response.created_at,
        model: response.model,
        promptId: response.prompt_id,
        promptText: getHistoricalPromptText(response.prompt_id, response.prompt_text_snapshot, promptById),
        responseText: response.response_text,
        citations: parseCitations(response.citations, response.response_text),
        mentionedBrandDomains,
        positionsByDomain,
      } satisfies ResponseInsight;
    });
  }, [effectiveFilteredResponses, brandEntities, promptById, workspaceBrandDomain]);

  const legacyResponseMentionsBrand = useCallback(
    (response: ResponseRow, brandDomain: string) => {
      if (!brandDomain) return false;
      if (brandDomain === workspaceBrandDomain) return response.brand_mentioned;

      const rawMentions = response.competitors_mentioned;
      if (!Array.isArray(rawMentions)) return false;

      return rawMentions.some((item) => {
        if (!item || typeof item !== 'object') return false;
        const mention = item as CompetitorMentionEntry;
        const domain = typeof mention.domain === 'string' ? normalizeDomain(mention.domain) : '';
        return mention.mentioned === true && domainMatches(domain, brandDomain);
      });
    },
    [workspaceBrandDomain]
  );

  const gapResponseIdSet = useMemo(() => {
    const result = new Set<string>();
    if (!workspaceBrandDomain) return result;

    for (const insight of responseInsights) {
      const mentionedDomainSet = new Set(insight.mentionedBrandDomains);
      if (mentionedDomainSet.has(workspaceBrandDomain)) continue;

      const hasCompetitor = Array.from(mentionedDomainSet).some((domain) =>
        activeGapCompetitorDomainSet.has(domain)
      );
      if (hasCompetitor) {
        result.add(insight.id);
      }
    }

    return result;
  }, [responseInsights, workspaceBrandDomain, activeGapCompetitorDomainSet]);

  const scopedResponseInsights = useMemo(() => {
    if (!gapOnly) return responseInsights;
    return responseInsights.filter((insight) => gapResponseIdSet.has(insight.id));
  }, [responseInsights, gapOnly, gapResponseIdSet]);

  const scopedResponseRows = useMemo(() => {
    if (!gapOnly) return effectiveFilteredResponses;
    return effectiveFilteredResponses.filter((response) => gapResponseIdSet.has(response.id));
  }, [effectiveFilteredResponses, gapOnly, gapResponseIdSet]);

  const completedRunIdsInRange = useMemo(() => {
    const matchingRunIds = promptRuns.flatMap((run) => {
      const completedAt = run.completed_at || run.created_at;
      return completedAt && isWithinDateRange(completedAt, rangeStart, rangeEnd) ? [run.id] : [];
    });

    return new Set(matchingRunIds);
  }, [promptRuns, rangeEnd, rangeStart]);

  const latestCompletedRunIdInRange = useMemo(() => {
    if (appliedPreset === 'today') {
      return promptRuns[0]?.id || null;
    }

    const matchingRun = promptRuns.find((run) => {
      const completedAt = run.completed_at || run.created_at;
      return completedAt && isWithinDateRange(completedAt, rangeStart, rangeEnd);
    });

    return matchingRun?.id || null;
  }, [appliedPreset, promptRuns, rangeEnd, rangeStart]);

  const summaryEligibleResponseIds = useMemo(() => {
    if (appliedPreset === 'today' && latestCompletedRunIdInRange) {
      return new Set(
        scopedResponseRows
          .filter((response) => response.run_id === latestCompletedRunIdInRange)
          .map((response) => response.id)
      );
    }

    if (completedRunIdsInRange.size > 0) {
      return new Set(
        scopedResponseRows
          .filter((response) => response.run_id && completedRunIdsInRange.has(response.run_id))
          .map((response) => response.id)
      );
    }

    if (selectedPromptId !== 'all') {
      return new Set(scopedResponseRows.map((response) => response.id));
    }

    return new Set(
      scopedResponseRows
        .filter((response) => !!response.prompt_id && trackedPromptIdSet.has(response.prompt_id))
        .map((response) => response.id)
    );
  }, [appliedPreset, completedRunIdsInRange, latestCompletedRunIdInRange, scopedResponseRows, selectedPromptId, trackedPromptIdSet]);

  const summaryResponseInsights = useMemo(() => {
    return responseInsights.filter((insight) => summaryEligibleResponseIds.has(insight.id));
  }, [responseInsights, summaryEligibleResponseIds]);

  const summaryResponseIdSet = useMemo(
    () => new Set(summaryResponseInsights.map((insight) => insight.id)),
    [summaryResponseInsights]
  );

  const scopedStructuredMentions = useMemo(() => {
    if (!gapOnly) return effectiveFilteredStructuredMentions;
    return effectiveFilteredStructuredMentions.filter((row) => gapResponseIdSet.has(row.prompt_response_id));
  }, [effectiveFilteredStructuredMentions, gapOnly, gapResponseIdSet]);

  const scopedStructuredCitations = useMemo(() => {
    if (!gapOnly) return effectiveFilteredStructuredCitations;
    return effectiveFilteredStructuredCitations.filter((row) => gapResponseIdSet.has(row.prompt_response_id));
  }, [effectiveFilteredStructuredCitations, gapOnly, gapResponseIdSet]);

  const structuredCitationsByResponseId = useMemo(() => {
    const map = new Map<string, StructuredCitationRow[]>();
    for (const citation of scopedStructuredCitations) {
      const existing = map.get(citation.prompt_response_id) || [];
      existing.push(citation);
      map.set(citation.prompt_response_id, existing);
    }
    return map;
  }, [scopedStructuredCitations]);

  const structuredMentionsByResponseId = useMemo(() => {
    const map = new Map<string, StructuredMentionRow[]>();
    for (const mention of scopedStructuredMentions) {
      const existing = map.get(mention.prompt_response_id) || [];
      existing.push(mention);
      map.set(mention.prompt_response_id, existing);
    }
    return map;
  }, [scopedStructuredMentions]);

  const getMentionStatusForResponse = useCallback(
    (response: ResponseRow, brandDomain: string): SearchResponseModalItem['mentionStatus'] => {
      if (!brandDomain) return 'none';

      const normalizedBrandDomain = normalizeDomain(brandDomain);
      const responseMentions = structuredMentionsByResponseId.get(response.id) || [];
      const responseCitations = structuredCitationsByResponseId.get(response.id) || [];

      const hasStructuredMention = responseMentions.some((row) =>
        domainMatches(row.brand_domain || '', normalizedBrandDomain)
      );
      const hasDirectCitation = responseCitations.some((row) => {
        const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
        return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
          && domainMatches(row.citation_domain || '', normalizedBrandDomain);
      });
      const hasIndirectCitation = responseCitations.some((row) => {
        const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
        return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
          && !domainMatches(row.citation_domain || '', normalizedBrandDomain);
      });

      if (hasDirectCitation) return 'mentioned';
      if (hasIndirectCitation && hasStructuredMention) return 'indirect';

      if (normalizedBrandDomain === workspaceBrandDomain && response.brand_mentioned) {
        return hasIndirectCitation ? 'indirect' : 'mentioned';
      }

      if (Array.isArray(response.competitors_mentioned)) {
        const legacyMention = (response.competitors_mentioned as Array<Record<string, unknown>>).some((raw) => {
          const mentioned = raw.mentioned === true;
          const domain = typeof raw.domain === 'string' ? normalizeDomain(raw.domain) : '';
          return mentioned && domainMatches(domain, normalizedBrandDomain);
        });

        if (legacyMention) {
          return hasIndirectCitation ? 'indirect' : 'mentioned';
        }
      }

      if (hasStructuredMention) {
        return hasIndirectCitation ? 'indirect' : 'mentioned';
      }

      return 'none';
    },
    [structuredCitationsByResponseId, structuredMentionsByResponseId, workspaceBrandDomain]
  );

  const getBrandCitationRows = useCallback(
    (brandDomain: string, list: StructuredCitationRow[]) =>
      list.filter((citation) => citationMatchesBrand(citation, brandDomain, brandEntities)),
    [brandEntities]
  );

  const buildBrandMentionBreakdown = useCallback(
    (
      brandDomain: string,
      mentionRows: StructuredMentionRow[],
      citationRows: StructuredCitationRow[],
      responseRows: ResponseRow[]
    ) => {
      const totals = { total: 0, direct: 0, indirect: 0 };
      const brandMentions = mentionRows.filter((mention) => domainMatches(mention.brand_domain, brandDomain));
      const responseRowById = new Map(responseRows.map((response) => [response.id, response] as const));
      const citationResponseIds = new Set<string>();

      if (citationRows.length > 0) {
        citationRows.forEach((citation) => {
          citationResponseIds.add(citation.prompt_response_id);
          totals.total += 1;

          if (citationIsDirectForBrand(citation, brandDomain)) {
            totals.direct += 1;
          } else {
            totals.indirect += 1;
          }
        });
      }

      const applyCount = (count: number, responseId: string) => {
        const brandCitations = citationRows.filter((citation) => citation.prompt_response_id === responseId);
        if (brandCitations.some((citation) => citationIsDirectForBrand(citation, brandDomain))) {
          totals.direct += count;
          return;
        }

        if (brandCitations.length > 0) {
          totals.indirect += count;
          return;
        }

        const response = responseRowById.get(responseId);
        const legacyCitationDomains = toStringArray(response?.citations).map(normalizeDomain);

        if (legacyCitationDomains.some((domain) => domainMatches(domain, brandDomain))) {
          totals.direct += count;
        } else {
          totals.indirect += count;
        }
      };

      if (brandMentions.length > 0) {
        brandMentions.forEach((mention) => {
          if (citationResponseIds.has(mention.prompt_response_id)) return;

          const count = Math.max(mention.mention_count || 0, 1);
          totals.total += count;
          applyCount(count, mention.prompt_response_id);
        });
        return totals;
      }

      responseRows
        .filter((response) => legacyResponseMentionsBrand(response, brandDomain))
        .forEach((response) => {
          if (citationResponseIds.has(response.id)) return;

          totals.total += 1;
          applyCount(1, response.id);
        });

      return totals;
    },
    [legacyResponseMentionsBrand]
  );

  const brandEntityByDomain = useMemo(
    () => new Map(brandEntities.map((brand) => [brand.domain, brand] as const)),
    [brandEntities]
  );

  const getCitationMatchedBrandDomains = useCallback(
    (citation: StructuredCitationRow) =>
      brandEntities
        .filter((brand) => citationMatchesBrand(citation, brand.domain, brandEntities))
        .map((brand) => brand.domain),
    [brandEntities]
  );

  const brandRows = useMemo(() => {
    if (hasStructuredMentions) {
      const brandMetaByDomain = new Map(
        brandEntities.map((entity) => [entity.domain, entity] as const)
      );
      const stats = new Map<string, {
        name: string;
        domain: string;
        faviconUrl: string;
        mentions: number;
        promptIds: Set<string>;
        positions: number[];
        citedLinks: Set<string>;
        latestPromptText: string;
        latestPromptAt: string;
      }>();

      for (const mention of scopedStructuredMentions) {
        const domain = normalizeDomain(mention.brand_domain);
        if (!domain) continue;
        const fromEntity = brandMetaByDomain.get(domain);
        const existing = stats.get(domain) || {
          name: fromEntity?.name || mention.brand_name || extractBrandName(domain),
          domain,
          faviconUrl:
            fromEntity?.faviconUrl ||
            `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
          mentions: 0,
          promptIds: new Set<string>(),
          positions: [],
          citedLinks: new Set<string>(),
          latestPromptText: 'Prompt unavailable',
          latestPromptAt: '',
        };

        existing.mentions += Math.max(1, mention.mention_count || 1);
        if (mention.prompt_id) {
          existing.promptIds.add(mention.prompt_id);
        }
        const promptText = getHistoricalPromptText(
          mention.prompt_id,
          responseById.get(mention.prompt_response_id)?.prompt_text_snapshot,
          promptById
        );
        if (!existing.latestPromptAt || mention.created_at > existing.latestPromptAt) {
          existing.latestPromptAt = mention.created_at;
          existing.latestPromptText = promptText;
        }
        if (typeof mention.position === 'number' && Number.isFinite(mention.position)) {
          existing.positions.push(mention.position);
        }
        stats.set(domain, existing);
      }

      for (const citation of scopedStructuredCitations) {
        const matchedDomains = getCitationMatchedBrandDomains(citation);
        for (const domain of matchedDomains) {
          const fromEntity = brandMetaByDomain.get(domain);
          const entry = stats.get(domain) || {
            name: fromEntity?.name || extractBrandName(domain),
            domain,
            faviconUrl:
              fromEntity?.faviconUrl ||
              `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
            mentions: 0,
            promptIds: new Set<string>(),
            positions: [],
            citedLinks: new Set<string>(),
            latestPromptText: 'Prompt unavailable',
            latestPromptAt: '',
          };

          if (citation.prompt_id) {
            entry.promptIds.add(citation.prompt_id);
          }
          if (!entry.latestPromptAt || citation.created_at > entry.latestPromptAt) {
            entry.latestPromptAt = citation.created_at;
            entry.latestPromptText = getHistoricalPromptText(
              citation.prompt_id,
              responseById.get(citation.prompt_response_id)?.prompt_text_snapshot,
              promptById
            );
          }
          entry.citedLinks.add(citation.citation_url);
          stats.set(domain, entry);
        }
      }

      let rows = Array.from(stats.values())
        .map((item) => {
          const breakdown = buildBrandMentionBreakdown(
            item.domain,
            scopedStructuredMentions,
            getBrandCitationRows(item.domain, scopedStructuredCitations),
            scopedResponseRows
          );

          return {
        name: item.name,
        domain: item.domain,
        faviconUrl: item.faviconUrl,
        mentions: breakdown.total,
        promptCount: item.promptIds.size,
        avgPosition:
          item.positions.length > 0
            ? item.positions.reduce((sum, value) => sum + value, 0) / item.positions.length
            : null,
        citedLinks: item.citedLinks.size,
          };
        })
        .filter((item) => item.mentions > 0);

      rows = rows.filter((row) => activeBrandDomainSet.has(row.domain));
      return rows.sort((a, b) => b.mentions - a.mentions);
    }

    const stats = new Map<string, {
      entity: BrandEntity;
      mentions: number;
      promptIds: Set<string>;
      positions: number[];
      citedLinks: Set<string>;
      latestPromptText: string;
      latestPromptAt: string;
    }>();

    for (const entity of brandEntities) {
      stats.set(entity.domain, {
        entity,
        mentions: 0,
        promptIds: new Set<string>(),
        positions: [],
        citedLinks: new Set<string>(),
        latestPromptText: 'Prompt unavailable',
        latestPromptAt: '',
      });
    }

    for (const insight of scopedResponseInsights) {
      for (const domain of insight.mentionedBrandDomains) {
        const stat = stats.get(domain);
        if (!stat) continue;

        stat.mentions += 1;
        if (insight.promptId) {
          stat.promptIds.add(insight.promptId);
        }
        if (!stat.latestPromptAt || insight.createdAt > stat.latestPromptAt) {
          stat.latestPromptAt = insight.createdAt;
          stat.latestPromptText = insight.promptText;
        }
        const position = insight.positionsByDomain.get(domain);
        if (typeof position === 'number') {
          stat.positions.push(position);
        }
        for (const citation of insight.citations) {
          stat.citedLinks.add(citation);
        }
      }

      for (const citation of insight.citations) {
        const citationDomain = normalizeDomain(citation);
        const stat = stats.get(citationDomain);
        if (!stat) continue;
        if (insight.mentionedBrandDomains.includes(citationDomain)) continue;

        stat.mentions += 1;
        if (insight.promptId) {
          stat.promptIds.add(insight.promptId);
        }
        if (!stat.latestPromptAt || insight.createdAt > stat.latestPromptAt) {
          stat.latestPromptAt = insight.createdAt;
          stat.latestPromptText = insight.promptText;
        }
        stat.citedLinks.add(citation);
      }
    }

    const rows = Array.from(stats.values())
      .filter((item) => item.mentions > 0)
      .map((item) => {
        const avgPosition =
          item.positions.length > 0
            ? item.positions.reduce((sum, value) => sum + value, 0) / item.positions.length
            : null;
        return {
          name: item.entity.name,
          domain: item.entity.domain,
          faviconUrl: item.entity.faviconUrl,
          mentions: item.mentions,
          promptCount: item.promptIds.size,
          avgPosition,
          citedLinks: item.citedLinks.size,
        } satisfies BrandRow;
      });

    let filtered = rows.filter((row) => activeBrandDomainSet.has(row.domain));
    return filtered.sort((a, b) => b.mentions - a.mentions);
  }, [
    brandEntities,
    scopedResponseInsights,
    hasStructuredMentions,
    scopedStructuredMentions,
    scopedStructuredCitations,
    activeBrandDomainSet,
    buildBrandMentionBreakdown,
    getCitationMatchedBrandDomains,
    getBrandCitationRows,
    promptById,
    responseById,
    scopedResponseRows,
  ]);

  const mentionRows = useMemo(() => {
    if (hasStructuredCitations) {
      const brandByDomain = new Map(brandEntities.map((brand) => [brand.domain, brand.name]));
      const mentionMap = new Map<string, {
        url: string;
        domain: string;
        title: string;
        sourceType: 'Direct Brand' | 'Social' | 'Government' | 'News' | 'Tech';
        count: number;
        brands: Set<string>;
        brandNames: Set<string>;
        details: MentionDetail[];
      }>();

      for (const citation of scopedStructuredCitations) {
        const url = citation.citation_url;
        const domain = normalizeDomain(citation.citation_domain || citation.citation_url);
        if (!url || !domain) continue;

        const matchedBrandDomains = getCitationMatchedBrandDomains(citation);
        const response = responseById.get(citation.prompt_response_id);

        const entry = mentionMap.get(url) || {
          url,
          domain,
          title: decodeHtmlEntities(citation.citation_title) || getCitationDisplayTitle(url, domain),
          sourceType: mapStoredSourceCategory(citation.source_category, citation.source_type),
          count: 0,
          brands: new Set<string>(),
          brandNames: new Set<string>(),
          details: [],
        };

        entry.count += 1;

        for (const brandDomain of matchedBrandDomains) {
          if (!brandDomain) continue;
          entry.brands.add(brandDomain);
          entry.brandNames.add(brandByDomain.get(brandDomain) || extractBrandName(brandDomain));
        }

        entry.details.push({
          responseId: citation.prompt_response_id,
          createdAt: citation.created_at,
          model: citation.model,
          promptText: getHistoricalPromptText(
            citation.prompt_id,
            response?.prompt_text_snapshot,
            promptById
          ),
          responseText: response?.response_text || 'Raw output unavailable.',
          snippet: citation.context_snippet || undefined,
        });

        mentionMap.set(url, entry);
      }

      let rows = Array.from(mentionMap.values()).map((item) => {
        const sortedDetails = item.details.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        const preferredPromptDetail =
          sortedDetails.find((detail) => detail.promptText && detail.promptText !== 'Prompt unavailable') ?? sortedDetails[0];
        const rawBrandDomains = Array.from(item.brands);
        const brandDomains = rawBrandDomains.length > 0 ? rawBrandDomains : [item.domain];
        const brandNames =
          rawBrandDomains.length > 0
            ? brandDomains.map((brandDomain) => brandByDomain.get(brandDomain) || extractBrandName(brandDomain))
            : Array.from(item.brandNames).length > 0
              ? Array.from(item.brandNames)
              : [extractBrandName(item.domain)];
        return {
          url: item.url,
          domain: item.domain,
          title: decodeHtmlEntities(item.title) || getCitationDisplayTitle(item.url, item.domain),
          faviconUrl: `https://www.google.com/s2/favicons?domain=${item.domain}&sz=64`,
          mentionCategory: detectMentionCategory(item.domain, brandDomains),
          sourceType: item.sourceType,
          count: item.count,
          promptText: preferredPromptDetail?.promptText || 'Prompt unavailable',
          brandDomains,
          brandNames,
          details: sortedDetails,
        } satisfies MentionLinkRow;
      });

      rows = rows.filter(
        (row) =>
          row.brandDomains.some((domain) => activeBrandDomainSet.has(domain)) ||
          activeBrandDomainSet.has(row.domain)
      );

      return rows.sort((a, b) => b.count - a.count);
    }
    const brandByDomain = new Map(brandEntities.map((brand) => [brand.domain, brand.name]));

    const map = new Map<string, {
      url: string;
      domain: string;
      title: string;
      count: number;
      brands: Set<string>;
      details: MentionDetail[];
    }>();

    for (const insight of scopedResponseInsights) {
      for (const url of insight.citations) {
        const domain = normalizeDomain(url);
        if (!domain) continue;

        const key = url;
        const existing = map.get(key) || {
          url,
          domain,
          title: getCitationDisplayTitle(url, domain),
          count: 0,
          brands: new Set<string>(),
          details: [],
        };

        existing.count += 1;
        for (const brandDomain of insight.mentionedBrandDomains) {
          existing.brands.add(brandDomain);
        }
        existing.details.push({
          responseId: insight.id,
          createdAt: insight.createdAt,
          model: insight.model,
          promptText: insight.promptText,
          responseText: insight.responseText,
        });

        map.set(key, existing);
      }
    }

    let rows = Array.from(map.values()).map((item) => {
      const sortedDetails = item.details.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      const rawBrandDomains = Array.from(item.brands);
      const brandDomains = rawBrandDomains.length > 0 ? rawBrandDomains : [item.domain];
      return {
        url: item.url,
        domain: item.domain,
          title: decodeHtmlEntities(item.title) || getCitationDisplayTitle(item.url, item.domain),
        faviconUrl: `https://www.google.com/s2/favicons?domain=${item.domain}&sz=64`,
        mentionCategory: detectMentionCategory(item.domain, brandDomains),
        sourceType: 'Tech',
        count: item.count,
        promptText: sortedDetails[0]?.promptText || 'Prompt unavailable',
        brandDomains,
        brandNames: brandDomains.map((domain) => brandByDomain.get(domain) || extractBrandName(domain)),
        details: sortedDetails,
      } satisfies MentionLinkRow;
    });

    rows = rows.filter(
      (row) =>
        row.brandDomains.some((domain) => activeBrandDomainSet.has(domain)) ||
        activeBrandDomainSet.has(row.domain)
    );

    return rows.sort((a, b) => b.count - a.count);
  }, [
    brandEntities,
    scopedResponseInsights,
    hasStructuredCitations,
    scopedStructuredCitations,
    getCitationMatchedBrandDomains,
    promptById,
    responseById,
    activeBrandDomainSet,
  ]);

  const promptMentionRows = useMemo<PromptMentionRow[]>(() => {
    type PromptMentionDraft = {
      promptId: string | null;
      promptText: string;
      model: string;
      mentionCount: number;
      directCount: number;
      indirectCount: number;
      responseIds: Set<string>;
      brandDomains: Set<string>;
      brandNames: Set<string>;
      lastSeen: string;
      latestResponseId: string | null;
      latestModel: string;
      latestResponseText: string;
    };

    const rowsByPromptModel = new Map<string, PromptMentionDraft>();
    const brandNameByDomain = new Map(brandEntities.map((brand) => [brand.domain, brand.name] as const));

    const getOrCreatePromptRow = (
      promptKey: string,
      promptId: string | null,
      promptText: string,
      model: string
    ) => {
      const existing = rowsByPromptModel.get(promptKey);
      if (existing) return existing;

      const created: PromptMentionDraft = {
        promptId,
        promptText,
        model,
        mentionCount: 0,
        directCount: 0,
        indirectCount: 0,
        responseIds: new Set<string>(),
        brandDomains: new Set<string>(),
        brandNames: new Set<string>(),
        lastSeen: '',
        latestResponseId: null,
        latestModel: '',
        latestResponseText: '',
      };
      rowsByPromptModel.set(promptKey, created);
      return created;
    };

    if (hasStructuredMentions || hasStructuredCitations) {
      const citationCoveredResponseBrands = new Set<string>();

      for (const citation of scopedStructuredCitations) {
        const matchingDomains = getCitationMatchedBrandDomains(citation).filter((domain) =>
          activeBrandDomainSet.has(domain)
        );
        if (matchingDomains.length === 0) continue;

        const promptId = citation.prompt_id;
        const promptKey = `${promptId ?? `deleted:${citation.prompt_response_id}`}:${citation.model}`;
        const promptText = getHistoricalPromptText(
          promptId,
          responseById.get(citation.prompt_response_id)?.prompt_text_snapshot,
          promptById
        );
        const existing = getOrCreatePromptRow(promptKey, promptId, promptText, citation.model);

        existing.mentionCount += matchingDomains.length;
        existing.responseIds.add(citation.prompt_response_id);
        matchingDomains.forEach((brandDomain) => {
          citationCoveredResponseBrands.add(`${citation.prompt_response_id}:${brandDomain}`);
          if (citationIsDirectForBrand(citation, brandDomain)) {
            existing.directCount += 1;
          } else {
            existing.indirectCount += 1;
          }
          existing.brandDomains.add(brandDomain);
          existing.brandNames.add(brandNameByDomain.get(brandDomain) || extractBrandName(brandDomain));
        });
        if (!existing.lastSeen || citation.created_at > existing.lastSeen) {
          existing.lastSeen = citation.created_at;
          existing.latestResponseId = citation.prompt_response_id;
          existing.latestModel = citation.model;
          existing.latestResponseText =
            responseById.get(citation.prompt_response_id)?.response_text || 'Raw output unavailable.';
        }
      }

      for (const mention of scopedStructuredMentions) {
        const brandDomain = normalizeDomain(mention.brand_domain);
        if (!brandDomain || !activeBrandDomainSet.has(brandDomain)) continue;
        if (citationCoveredResponseBrands.has(`${mention.prompt_response_id}:${brandDomain}`)) continue;

        const promptId = mention.prompt_id;
        const promptKey = `${promptId ?? `deleted:${mention.prompt_response_id}`}:${mention.model}`;
        const promptText = getHistoricalPromptText(
          promptId,
          responseById.get(mention.prompt_response_id)?.prompt_text_snapshot,
          promptById
        );
        const existing = getOrCreatePromptRow(promptKey, promptId, promptText, mention.model);
        const mentionCount = Math.max(1, mention.mention_count || 1);
        const response = responseById.get(mention.prompt_response_id);
        const legacyCitationDomains = toStringArray(response?.citations).map(normalizeDomain);
        const isDirect = legacyCitationDomains.some((domain) => domainMatches(domain, brandDomain));

        existing.mentionCount += mentionCount;
        if (isDirect) {
          existing.directCount += mentionCount;
        } else {
          existing.indirectCount += mentionCount;
        }
        existing.responseIds.add(mention.prompt_response_id);
        existing.brandDomains.add(brandDomain);
        existing.brandNames.add(
          brandNameByDomain.get(brandDomain) || mention.brand_name || extractBrandName(brandDomain)
        );
        if (!existing.lastSeen || mention.created_at > existing.lastSeen) {
          existing.lastSeen = mention.created_at;
          existing.latestResponseId = mention.prompt_response_id;
          existing.latestModel = mention.model;
          existing.latestResponseText =
            responseById.get(mention.prompt_response_id)?.response_text || 'Raw output unavailable.';
        }
      }
    } else {
      for (const insight of scopedResponseInsights) {
        const matchingDomains = insight.mentionedBrandDomains.filter((domain) =>
          activeBrandDomainSet.has(domain)
        );
        if (matchingDomains.length === 0) continue;

        const promptKey = `${insight.promptId ?? `deleted:${insight.id}`}:${insight.model}`;
        const existing = rowsByPromptModel.get(promptKey) || {
          promptId: insight.promptId,
          promptText: insight.promptText,
          model: insight.model,
          mentionCount: 0,
          directCount: 0,
          indirectCount: 0,
          responseIds: new Set<string>(),
          brandDomains: new Set<string>(),
          brandNames: new Set<string>(),
          lastSeen: '',
          latestResponseId: null,
          latestModel: '',
          latestResponseText: '',
        };

        existing.mentionCount += matchingDomains.length;
        existing.responseIds.add(insight.id);
        matchingDomains.forEach((domain) => {
          const isDirect = insight.citations.map(normalizeDomain).some((citationDomain) =>
            domainMatches(citationDomain, domain)
          );
          if (isDirect) {
            existing.directCount += 1;
          } else {
            existing.indirectCount += 1;
          }
          existing.brandDomains.add(domain);
          existing.brandNames.add(brandNameByDomain.get(domain) || extractBrandName(domain));
        });
        if (!existing.lastSeen || insight.createdAt > existing.lastSeen) {
          existing.lastSeen = insight.createdAt;
          existing.latestResponseId = insight.id;
          existing.latestModel = insight.model;
          existing.latestResponseText = insight.responseText;
        }

        rowsByPromptModel.set(promptKey, existing);
      }
    }

    return Array.from(rowsByPromptModel.values())
      .map((row) => {
        const responseSpecificBrands = row.latestResponseId
          ? (() => {
              const structuredMentionRows = structuredMentionsByResponseId.get(row.latestResponseId) || [];
              const structuredCitationRows = structuredCitationsByResponseId.get(row.latestResponseId) || [];
              const activeDomainSet = new Set<string>();

              structuredMentionRows
                .map((mention) => normalizeDomain(mention.brand_domain))
                .filter((domain) => domain && activeBrandDomainSet.has(domain))
                .forEach((domain) => activeDomainSet.add(domain));

              structuredCitationRows
                .flatMap((citation) => getCitationMatchedBrandDomains(citation))
                .filter((domain) => activeBrandDomainSet.has(domain))
                .forEach((domain) => activeDomainSet.add(domain));

              if (activeDomainSet.size > 0) {
                return Array.from(activeDomainSet).map((domain) => {
                  const matchedBrand = brandEntityByDomain.get(domain);
                  return (
                    matchedBrand || {
                      domain,
                      name: extractBrandName(domain),
                      faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
                      isWorkspaceBrand: false,
                    }
                  );
                });
              }

              return [];
            })()
          : [];

        return {
          rowKey: `${row.promptId ?? row.latestResponseId ?? row.promptText}:${row.model}:${row.lastSeen}`,
          promptId: row.promptId,
          promptText: row.promptText,
          model: row.model,
          mentionCategory: row.directCount > 0 ? 'Direct' as const : 'Indirect' as const,
          mentionCount: row.mentionCount,
          brandDomains: Array.from(row.brandDomains),
          brandNames: Array.from(row.brandNames),
          responseBrandDomains: responseSpecificBrands.map((brand) => brand.domain),
          responseBrandNames: responseSpecificBrands.map((brand) => brand.name),
          lastSeen: row.lastSeen,
          modalItem: row.latestResponseId
            ? {
                id: row.latestResponseId,
                promptText: row.promptText,
                createdAt: row.lastSeen,
                model: row.latestModel,
                responseText: row.latestResponseText,
                brands: responseSpecificBrands,
                citations: (() => {
                  const response = responseById.get(row.latestResponseId);
                  const citationMap = new Map<string, { url: string; domain: string }>();
                  const structuredRows = structuredCitationsByResponseId.get(row.latestResponseId) || [];

                  structuredRows.forEach((citation) => {
                    const url = typeof citation.citation_url === 'string' ? citation.citation_url : '';
                    const domain =
                      typeof citation.citation_domain === 'string'
                        ? normalizeDomain(citation.citation_domain)
                        : normalizeDomain(url);
                    if (!url || citationMap.has(url)) return;
                    citationMap.set(url, { url, domain });
                  });

                  const storedCitations = response && Array.isArray(response.citations)
                    ? response.citations.filter((item): item is string => typeof item === 'string')
                    : [];

                  storedCitations.forEach((url) => {
                    if (!url || citationMap.has(url)) return;
                    citationMap.set(url, { url, domain: normalizeDomain(url) });
                  });

                  return Array.from(citationMap.values());
                })(),
                responseLinks: (() => {
                  const response = responseById.get(row.latestResponseId);
                  const citationMap = new Map<string, { url: string; domain: string }>();
                  const structuredRows = structuredCitationsByResponseId.get(row.latestResponseId) || [];

                  structuredRows.forEach((citation) => {
                    const url = typeof citation.citation_url === 'string' ? citation.citation_url : '';
                    const domain =
                      typeof citation.citation_domain === 'string'
                        ? normalizeDomain(citation.citation_domain)
                        : normalizeDomain(url);
                    if (!url || citationMap.has(url)) return;
                    citationMap.set(url, { url, domain });
                  });

                  const storedCitations = response && Array.isArray(response.citations)
                    ? response.citations.filter((item): item is string => typeof item === 'string')
                    : [];

                  storedCitations.forEach((url) => {
                    if (!url || citationMap.has(url)) return;
                    citationMap.set(url, { url, domain: normalizeDomain(url) });
                  });

                  return mergeResponseLinks(
                    Array.from(citationMap.values()),
                    parseProviderLinks(response?.provider_links)
                  );
                })(),
                mentionStatus: (() => {
                  const response = responseById.get(row.latestResponseId);
                  if (!response || !workspaceBrandDomain) return 'none';
                  return getMentionStatusForResponse(response, workspaceBrandDomain);
                })(),
              }
            : null,
        };
      })
      .sort((a, b) => {
        if (b.mentionCount !== a.mentionCount) return b.mentionCount - a.mentionCount;
        return b.lastSeen.localeCompare(a.lastSeen);
      });
  }, [
    activeBrandDomainSet,
    brandEntities,
    brandEntityByDomain,
    getCitationMatchedBrandDomains,
    getMentionStatusForResponse,
    hasStructuredCitations,
    hasStructuredMentions,
    promptById,
    responseById,
    scopedResponseInsights,
    scopedStructuredCitations,
    scopedStructuredMentions,
    structuredCitationsByResponseId,
    structuredMentionsByResponseId,
  ]);

  const trackedBrandDomains = useMemo(
    () => trackedFilterBrands.map((brand) => brand.domain),
    [trackedFilterBrands]
  );

  const selectedTrackedBrandDomains = useMemo(
    () => selectedBrandDomains.filter((domain) => trackedBrandDomains.includes(domain)),
    [selectedBrandDomains, trackedBrandDomains]
  );

  const trackedAllSelected =
    selectedBrandDomains.length === 0 ||
    selectedTrackedBrandDomains.length === 0 ||
    selectedTrackedBrandDomains.length === trackedBrandDomains.length;

  const toggleBrandDomainSelection = (
    domain: string,
    universe: string[],
    allSelected: boolean
  ) => {
    setSelectedBrandDomains((previous) => {
      const scopedPrevious = previous.filter((item) => universe.includes(item));
      const base = previous.filter((item) => !universe.includes(item));
      const isSelected = allSelected ? false : scopedPrevious.includes(domain);

      if (isSelected) {
        return [...base, ...scopedPrevious.filter((item) => item !== domain)];
      }

      return [...base, ...(allSelected ? [domain] : [...scopedPrevious, domain])];
    });
  };

  const selectAllBrandDomains = (domains: string[], onComplete?: () => void) => {
    setSelectedBrandDomains((previous) => previous.filter((domain) => !domains.includes(domain)));
    onComplete?.();
  };

  const trackedBrandLabel = trackedAllSelected
    ? 'Filter by brand'
    : selectedTrackedBrandDomains.length === 1
      ? trackedFilterBrands.find((brand) => brand.domain === selectedTrackedBrandDomains[0])?.name || 'Brand competitors'
      : `Brand competitors (${selectedTrackedBrandDomains.length})`;

  const selectedPromptLabel =
    selectedPromptId === 'all'
      ? 'All prompts'
      : prompts.find((prompt) => prompt.id === selectedPromptId)?.text || 'Prompt unavailable';

  const performanceBrand = useMemo(() => {
    if (selectedBrandDomains.length === 1) {
      const domain = selectedBrandDomains[0];
      const entity = brandEntityByDomain.get(domain);
      return {
        domain,
        name: entity?.name || extractBrandName(domain),
      };
    }

    if (!workspaceBrandDomain) return null;
    return {
      domain: workspaceBrandDomain,
      name: getWorkspaceBrandName(workspace),
    };
  }, [brandEntityByDomain, selectedBrandDomains, workspace, workspaceBrandDomain]);

  const getPromptSearchCountForBrand = useCallback(
    (brandDomain: string | null) => {
      if (!brandDomain) return 0;

      if (hasStructuredMentions || hasStructuredCitations) {
        const responseIds = new Set<string>();

        filteredStructuredMentions.forEach((mention) => {
          if (!summaryResponseIdSet.has(mention.prompt_response_id)) return;
          if (domainMatches(mention.brand_domain, brandDomain)) {
            responseIds.add(mention.prompt_response_id);
          }
        });

        filteredStructuredCitations.forEach((citation) => {
          if (!summaryResponseIdSet.has(citation.prompt_response_id)) return;
          if (citationMatchesBrand(citation, brandDomain, brandEntities)) {
            responseIds.add(citation.prompt_response_id);
          }
        });

        return responseIds.size;
      }

      return summaryResponseInsights.filter((insight) =>
        insight.mentionedBrandDomains.some((domain) => domainMatches(domain, brandDomain))
      ).length;
    },
    [
      brandEntities,
      filteredStructuredCitations,
      filteredStructuredMentions,
      hasStructuredCitations,
      hasStructuredMentions,
      summaryResponseIdSet,
      summaryResponseInsights,
    ]
  );

  const performanceBrandPromptSearchCount = useMemo(
    () => getPromptSearchCountForBrand(performanceBrand?.domain || null),
    [getPromptSearchCountForBrand, performanceBrand?.domain]
  );

  const competitorPromptSearchCount = useMemo(() => {
    if (trackedCompetitorDomainSet.size === 0) return 0;

    if (hasStructuredMentions) {
      return new Set(
        filteredStructuredMentions
          .filter(
            (mention) =>
              summaryResponseIdSet.has(mention.prompt_response_id) &&
              trackedCompetitorDomainSet.has(normalizeDomain(mention.brand_domain))
          )
          .map((mention) => mention.prompt_response_id)
      ).size;
    }

    return summaryResponseInsights.filter((insight) =>
      insight.mentionedBrandDomains.some((domain) => trackedCompetitorDomainSet.has(domain))
    ).length;
  }, [trackedCompetitorDomainSet, hasStructuredMentions, filteredStructuredMentions, summaryResponseIdSet, summaryResponseInsights]);

  const totalPromptSearchCount = summaryResponseInsights.length;
  const performanceBrandVisibilityPercent = totalPromptSearchCount > 0
    ? (performanceBrandPromptSearchCount / totalPromptSearchCount) * 100
    : 0;

  const DEFAULT_PAGE_SIZE = 15;
  const CITATION_PAGE_SIZE = 10;
  const totalPromptPages = Math.max(1, Math.ceil(promptMentionRows.length / DEFAULT_PAGE_SIZE));
  const totalBrandPages = Math.max(1, Math.ceil(brandRows.length / DEFAULT_PAGE_SIZE));
  const totalCitationPages = Math.max(1, Math.ceil(mentionRows.length / CITATION_PAGE_SIZE));

  useEffect(() => {
    setPromptPage(1);
    setBrandPage(1);
    setCitationPage(1);
  }, [selectedPromptId, selectedBrandDomains, gapOnly, appliedPreset, rangeStart?.getTime(), rangeEnd?.getTime()]);

  useEffect(() => {
    if (promptPage > totalPromptPages) setPromptPage(totalPromptPages);
  }, [promptPage, totalPromptPages]);

  useEffect(() => {
    if (brandPage > totalBrandPages) setBrandPage(totalBrandPages);
  }, [brandPage, totalBrandPages]);

  useEffect(() => {
    if (citationPage > totalCitationPages) setCitationPage(totalCitationPages);
  }, [citationPage, totalCitationPages]);

  const paginatedPromptMentionRows = useMemo(() => {
    const start = (promptPage - 1) * DEFAULT_PAGE_SIZE;
    return promptMentionRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [promptMentionRows, promptPage]);

  const paginatedBrandRows = useMemo(() => {
    const start = (brandPage - 1) * DEFAULT_PAGE_SIZE;
    return brandRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [brandRows, brandPage]);

  const paginatedMentionRows = useMemo(() => {
    const start = (citationPage - 1) * CITATION_PAGE_SIZE;
    return mentionRows.slice(start, start + CITATION_PAGE_SIZE);
  }, [mentionRows, citationPage]);

  const hasPromptPagination = promptMentionRows.length > DEFAULT_PAGE_SIZE;
  const hasBrandPagination = brandRows.length > DEFAULT_PAGE_SIZE;
  const hasCitationPagination = mentionRows.length > CITATION_PAGE_SIZE;

  const summaryMentionBreakdown = useMemo(() => {
    const totals = { total: 0, direct: 0, indirect: 0 };

    activeBrandDomainSet.forEach((brandDomain) => {
      const breakdown = buildBrandMentionBreakdown(
        brandDomain,
        scopedStructuredMentions,
        getBrandCitationRows(brandDomain, scopedStructuredCitations),
        scopedResponseRows
      );

      totals.total += breakdown.total;
      totals.direct += breakdown.direct;
      totals.indirect += breakdown.indirect;
    });

    return totals;
  }, [
    activeBrandDomainSet,
    buildBrandMentionBreakdown,
    getBrandCitationRows,
    scopedResponseRows,
    scopedStructuredCitations,
    scopedStructuredMentions,
  ]);

  const mentionsByCategoryData = useMemo<DonutDatum[]>(() => {
    return [
      {
        label: 'Direct',
        value: summaryMentionBreakdown.direct,
        color: '#ffcb10',
        description:
          "Direct mentions are when an LLM directly cites the brand's own domain in its response.",
      },
      {
        label: 'Indirect',
        value: summaryMentionBreakdown.indirect,
        color: '#1078ff',
        description:
          'Indirect mentions are when an LLM cites another source that mentions the brand.',
      },
    ];
  }, [summaryMentionBreakdown.direct, summaryMentionBreakdown.indirect]);

  const mentionSourceTypeData = useMemo<DonutDatum[]>(() => {
    const counts = new Map<string, number>();

    for (const row of mentionRows) {
      counts.set(row.sourceType, (counts.get(row.sourceType) || 0) + 1);
    }

    return SOURCE_CATEGORY_CONFIG.map((category) => ({
      label: category.label,
      value: counts.get(category.label) || 0,
      color: category.color,
      description: category.description,
    }));
  }, [mentionRows]);

  const selectedBrandEmptyCopy = selectedBrandDomains.length === 1
    ? 'selected brand'
    : 'selected brands';

  const activeViewTitle = useMemo(() => {
    const selectedBrandName =
      selectedBrandDomains.length === 1
        ? brandEntities.find((brand) => brand.domain === selectedBrandDomains[0])?.name ||
          extractBrandName(selectedBrandDomains[0])
        : null;

    if (viewMode === 'prompts') {
      if (selectedBrandName) {
        return `Prompts (${selectedBrandName} is mentioned)`;
      }

      return 'Prompts (brands are mentioned)';
    }
    if (viewMode === 'citations') {
      return selectedBrandName ? `Citation URLs for ${selectedBrandName}` : 'Citation URLs';
    }
    return selectedBrandName ? `${selectedBrandName} mentioned in answers` : 'Brands mentioned in answers';
  }, [brandEntities, selectedBrandDomains, viewMode]);

  const activeViewTooltipText = useMemo(() => {
    if (viewMode === 'prompts') {
      return 'See which prompts led to brand mentions in AI answers, which brands were mentioned, and how many total mentions each prompt produced.';
    }
    if (viewMode === 'citations') {
      return 'See which URLs were cited in AI answers, which brands were associated with those citations, and which sources show up most often.';
    }
    return 'See which brands were mentioned in AI answers, how often they appeared, how many searches mentioned them, and how many linked sources they picked up.';
  }, [viewMode]);

  const activeViewCount = useMemo(() => {
    if (viewMode === 'prompts') return promptMentionRows.length;
    if (viewMode === 'citations') return mentionRows.length;
    return brandRows.length;
  }, [viewMode, promptMentionRows.length, mentionRows.length, brandRows.length]);

  const pageTitle = useMemo(() => {
    if (selectedBrandDomains.length === 1) {
      const selectedDomain = selectedBrandDomains[0];
      return `Mentions for ${brandEntities.find((brand) => brand.domain === selectedDomain)?.name || extractBrandName(selectedDomain)}`;
    }
    if (selectedBrandDomains.length > 1) {
      return `Mentions for ${selectedBrandDomains.length} filtered brands`;
    }
    return 'Mentions';
  }, [brandEntities, selectedBrandDomains]);

  const hasActiveFilters = useMemo(() => {
    const brandFilterActive = !trackedAllSelected;
    const promptFilterActive = selectedPromptId !== 'all';
    return brandFilterActive || promptFilterActive;
  }, [trackedAllSelected, selectedPromptId]);

  const resetFilters = () => {
    setSelectedPromptId('all');
    selectAllBrandDomains(trackedBrandDomains);
    setIsPromptDropdownOpen(false);
    setIsTrackedBrandDropdownOpen(false);
  };

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="overview-header" style={{ marginBottom: '1rem' }}>
        <div className="overview-header-left">
          <h1>{pageTitle}</h1>
        </div>
        <div className="mentions-header-controls">
          {hasActiveFilters ? (
            <a
              href="#"
              className="small-link mentions-reset-filters-link"
              onClick={(event) => {
                event.preventDefault();
                resetFilters();
              }}
            >
              Reset filters
            </a>
          ) : null}
          <div className="date-range-wrapper mentions-filter-dropdown-wrap is-prompt-filter" ref={promptDropdownRef}>
            <button
              type="button"
              className="date-range-toggle mentions-filter-dropdown-toggle mentions-prompt-dropdown-toggle"
              onClick={() => setIsPromptDropdownOpen((value) => !value)}
            >
              <div className="overview-brand-dropdown-value">
                <div className="search-brand-filter-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 6h13" />
                    <path d="M8 12h13" />
                    <path d="M8 18h13" />
                    <path d="M3 6h.01" />
                    <path d="M3 12h.01" />
                    <path d="M3 18h.01" />
                  </svg>
                </div>
                <div className="text-size-small text-weight-medium text-colour-dark-brown mentions-filter-dropdown-label">
                  {selectedPromptLabel}
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isPromptDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {isPromptDropdownOpen && (
              <div className="workspace-dropdown overview-brand-dropdown mentions-filter-dropdown mentions-prompt-dropdown">
                <div className="workspace-dropdown-list">
                  <button
                    type="button"
                    className={`workspace-dropdown-item ${selectedPromptId === 'all' ? 'is-active' : ''}`}
                    onClick={() => {
                      setSelectedPromptId('all');
                      setIsPromptDropdownOpen(false);
                    }}
                  >
                    <div className="overview-brand-dropdown-item-main">
                      <div className="search-brand-filter-placeholder is-small">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 6h13" />
                          <path d="M8 12h13" />
                          <path d="M8 18h13" />
                          <path d="M3 6h.01" />
                          <path d="M3 12h.01" />
                          <path d="M3 18h.01" />
                        </svg>
                      </div>
                      <div className="text-size-small text-colour-dark-brown">All prompts</div>
                    </div>
                    {selectedPromptId === 'all' && (
                      <span className="mentions-dropdown-check">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </span>
                    )}
                  </button>
                  {prompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      type="button"
                      className={`workspace-dropdown-item ${selectedPromptId === prompt.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedPromptId(prompt.id);
                        setIsPromptDropdownOpen(false);
                      }}
                    >
                      <div className="text-size-small text-colour-dark-brown mentions-prompt-option-label">
                        {prompt.text}
                      </div>
                      {selectedPromptId === prompt.id && (
                        <span className="mentions-dropdown-check">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-wrapper mentions-filter-dropdown-wrap" ref={trackedBrandDropdownRef}>
            <button
              type="button"
              className="date-range-toggle mentions-filter-dropdown-toggle"
              onClick={() => setIsTrackedBrandDropdownOpen((value) => !value)}
            >
              <div className="overview-brand-dropdown-value">
                <div className="search-brand-filter-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <div className="text-size-small text-weight-medium text-colour-dark-brown mentions-filter-dropdown-label">{trackedBrandLabel}</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isTrackedBrandDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {isTrackedBrandDropdownOpen && (
              <div className="workspace-dropdown overview-brand-dropdown mentions-filter-dropdown">
                <div className="workspace-dropdown-list">
                  <button
                    type="button"
                    className={`workspace-dropdown-item ${trackedAllSelected ? 'is-active' : ''}`}
                    onClick={() => selectAllBrandDomains(trackedBrandDomains, () => setIsTrackedBrandDropdownOpen(false))}
                  >
                    <div className="overview-brand-dropdown-item-main">
                      <div className="search-brand-filter-placeholder is-small">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M2 12h20" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      </div>
                      <div className="text-size-small text-colour-dark-brown">Show all brands</div>
                    </div>
                    {trackedAllSelected && (
                      <span className="mentions-dropdown-check">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </span>
                    )}
                  </button>
                  {trackedFilterBrands.map((brand) => {
                    const isSelected = !trackedAllSelected && selectedTrackedBrandDomains.includes(brand.domain);
                    return (
                      <button
                        key={brand.domain}
                        type="button"
                        className={`workspace-dropdown-item ${isSelected ? 'is-active' : ''}`}
                        onClick={() =>
                          toggleBrandDomainSelection(
                            brand.domain,
                            trackedBrandDomains,
                            trackedAllSelected
                          )
                        }
                      >
                        <div className="overview-brand-dropdown-item-main">
                          <div
                            className="overview-brand-dropdown-icon"
                            style={{ backgroundImage: `url("${brand.faviconUrl}")` }}
                          />
                          <div className="text-size-small text-colour-dark-brown">{brand.name}</div>
                          {brand.isWorkspaceBrand && (
                            <span className="new-span is-ranking-you">You</span>
                          )}
                        </div>
                        {isSelected && (
                          <span className="mentions-dropdown-check">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-wrapper" ref={datePickerRef}>
            <div
              className="date-range-toggle"
              onClick={() => (datePickerVisible ? dismissDatePicker() : openDatePicker())}
            >
              <div className="date-range-selected">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect width="18" height="18" x="3" y="4" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                <div className="text-size-small text-weight-medium text-colour-dark-brown">{dateLabel}</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isDatePickerOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>

            {datePickerVisible && (
              <div className={`date-range-dropdown ${isDatePickerOpen ? 'is-visible' : ''}`}>
                <div className="date-range-presets">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      className={`date-range-preset ${activePreset === preset.key ? 'is-active' : ''}`}
                      onClick={() => handlePresetClick(preset.key)}
                      type="button"
                    >
                      {preset.label}
                      {activePreset === preset.key && (
                        <span className="date-range-preset-check">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div style={{ flex: 1 }}>
                  <div className="date-range-calendars">
                    <Calendar
                      year={leftYear}
                      month={leftMonth}
                      rangeStart={tempStart}
                      rangeEnd={tempEnd}
                      onDayClick={handleDayClick}
                      onPrevMonth={handlePrevMonth}
                      onNextMonth={handleNextMonth}
                      showPrevNav={true}
                      showNextNav={false}
                    />
                    <Calendar
                      year={rightYear}
                      month={rightMonth}
                      rangeStart={tempStart}
                      rangeEnd={tempEnd}
                      onDayClick={handleDayClick}
                      onPrevMonth={handlePrevMonth}
                      onNextMonth={handleNextMonth}
                      showPrevNav={false}
                      showNextNav={true}
                    />
                  </div>

                  <div className="date-range-footer">
                    <button className="button is-clear" onClick={handleCancel} type="button">
                      Cancel
                    </button>
                    <button
                      className="button is-apply"
                      onClick={handleApply}
                      disabled={activePreset === 'custom' && (!tempStart || !tempEnd)}
                      type="button"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mentions-summary-grid">
        <div className="dashboard-panel mentions-summary-panel">
          <div className="dashboard-panel-body mentions-summary-panel-body">
            <div className="mentions-summary-chart-column">
              <DonutChart title="Total Mentions" totalLabel="Mentions" data={mentionsByCategoryData} />
            </div>
            <div className="mentions-summary-divider" />
            <div className="mentions-summary-chart-column">
              <DonutChart title="Source Breakdown" totalLabel="Sources" data={mentionSourceTypeData} />
            </div>
          </div>
        </div>

        <div className="dashboard-panel mentions-summary-info-panel">
          <div className="dashboard-panel-body mentions-summary-info-body">
            <div className="mentions-summary-info-block">
              <PerformanceGauge
                title={`How ${performanceBrand?.name || getWorkspaceBrandName(workspace)} Performs`}
                percentage={performanceBrandVisibilityPercent}
              />
              <div className="text-size-small text-colour-alt-grey mentions-summary-info-copy">
                <span className="mentions-summary-info-emphasis">
                  {performanceBrand?.name || getWorkspaceBrandName(workspace)}
                </span>{' '}
                is mentioned in{' '}
                <span className="mentions-summary-info-emphasis">
                  {performanceBrandPromptSearchCount.toLocaleString()} out of {totalPromptSearchCount.toLocaleString()}
                </span>{' '}
                prompt searches.
              </div>
              {performanceBrand?.domain && performanceBrandPromptSearchCount > 0 && (
                <button
                  type="button"
                  className="button is-tiny is-white has-inner-shadow w-button"
                  onClick={() => {
                    setSelectedBrandDomains([performanceBrand.domain]);
                    setViewMode('prompts');
                  }}
                >
                  View mentions
                </button>
              )}
            </div>

            <div className="mentions-summary-info-block">
              <div className="text-size-small text-colour-alt-grey mentions-summary-info-copy">
                Your competitors are mentioned in{' '}
                <span className="mentions-summary-info-emphasis">
                  {competitorPromptSearchCount.toLocaleString()} out of {totalPromptSearchCount.toLocaleString()}
                </span>{' '}
                prompt searches.
              </div>
              <div className="text-size-xsmall text-weight-medium mentions-summary-info-note">
                Understand more in the table below.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginBottom: '1.5rem' }}>
        <div className="dashboard-panel-header" style={{ padding: '1rem 1.75rem' }}>
            <div className="table-title-wrapper is-left mentions-table-title-wrapper">
              <div className="dashboard-panel-title">{activeViewTitle}</div>
              <div className="number-pill">{activeViewCount}</div>
              <InfoTooltip
                text={activeViewTooltipText}
              />
            </div>
          <div className="mentions-header-actions">
            <div className={`mentions-toggle-group overview-chart-toggle ${
              viewMode === 'prompts' ? 'is-prompts' : viewMode === 'citations' ? 'is-citations' : 'is-brands'
            }`}>
              <button
                type="button"
                className={`overview-chart-toggle-button ${viewMode === 'prompts' ? 'is-active' : ''}`}
                onClick={() => setViewMode('prompts')}
              >
                Prompt sources
              </button>
              <button
                type="button"
                className={`overview-chart-toggle-button ${viewMode === 'citations' ? 'is-active' : ''}`}
                onClick={() => setViewMode('citations')}
              >
                Citation URLs
              </button>
              <button
                type="button"
                className={`overview-chart-toggle-button ${viewMode === 'brands' ? 'is-active' : ''}`}
                onClick={() => setViewMode('brands')}
              >
                Brand mentions
              </button>
            </div>
          </div>
        </div>
        <div className="dashboard-panel-body is-table">
          <div className="table-scroll-wrap">
          {viewMode === 'prompts' ? (
            <table key="mentions-prompts-table" className={`table-component mentions-table mentions-table--prompts ${hasPromptPagination ? 'has-pagination' : ''}`}>
              <thead className="table-header">
                <tr className="table-row is-header-row">
                  <th className="table-head is-prompt-text">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Prompt used</div>
                  </th>
                  <th className="table-head is-model">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">LLM</div>
                  </th>
                  <th className="table-head is-brands">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brands mentioned</div>
                  </th>
                  <th className="table-head is-category">
                    <div className="table-title-wrapper is-left mentions-column-tooltip-title">
                      <div className="text-size-small text-weight-semibold text-colour-alt-grey">Type</div>
                      <MentionsHeaderPortalTooltip text="Direct means the brand is cited or mentioned directly in the AI answer. Indirect means the brand is mentioned through a third-party cited source." />
                    </div>
                  </th>
                  <th className="table-head is-mentions-count">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Total mentions</div>
                  </th>
                  <th className="table-head is-action">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Action</div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {promptMentionRows.length === 0 ? (
                  <tr className="table-row">
                    <td className="table-cell" colSpan={6} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                      <div className="text-size-small text-colour-light-grey">
                        {gapOnly
                          ? `No gap prompt opportunities found for the ${selectedBrandEmptyCopy} yet.`
                          : `No prompt mentions found for the ${selectedBrandEmptyCopy} yet.`}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedPromptMentionRows.map((row) => {
                    const responseSpecificBrands = row.responseBrandDomains.map((domain, index) => {
                      const matchedBrand = brandEntities.find((brand) => brand.domain === domain);
                      const brandName = matchedBrand?.name || row.responseBrandNames[index] || extractBrandName(domain);
                      const faviconUrl =
                        matchedBrand?.faviconUrl ||
                        `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

                      return {
                        domain,
                        brandName,
                        faviconUrl,
                      };
                    });
                    const visibleResponseBrands = responseSpecificBrands.slice(0, 3);
                    const hiddenResponseBrands = responseSpecificBrands.slice(3);

                    return (
                    <tr key={row.rowKey} className="table-row">
                        <td className="table-cell is-prompt-text">
                          <div className="mentions-prompt-preview-wrap">
                            <div className="text-size-xsmall text-weight-medium text-colour-dark-brown mentions-prompt-preview">
                              {row.promptText}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell is-model">
                          <div className="text-size-xsmall text-weight-medium text-colour-alt-grey">
                            {formatModelLabel(row.model)}
                          </div>
                        </td>
                        <td className="table-cell is-brands">
                          <div className="mentions-prompt-brand-list">
                            {visibleResponseBrands.map(({ domain, brandName, faviconUrl }) => {
                              return (
                                <div key={`${row.promptId ?? row.promptText}-${domain}`} className="mentions-prompt-brand-item">
                                  <div
                                    className="mention-favicon"
                                    style={{ backgroundImage: `url(${faviconUrl})` }}
                                  />
                                  <div className="text-size-tiny text-weight-medium text-colour-alt-grey">{brandName}</div>
                                </div>
                              );
                            })}
                            {hiddenResponseBrands.length > 0 ? (
                              <div className="info-tooltip-wrapper is-align-center is-placement-top">
                                <div className="mentions-prompt-brand-item mentions-prompt-brand-item--overflow">
                                  <div className="text-size-tiny text-weight-medium text-colour-alt-grey">
                                    + {hiddenResponseBrands.length}
                                  </div>
                                </div>
                                <span className="info-tooltip mentions-brand-overflow-tooltip">
                                  {hiddenResponseBrands.map((brand) => brand.brandName).join(', ')}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="table-cell is-category">
                          <span
                            className={`mentions-source-tag ${
                              row.mentionCategory === 'Direct' ? 'is-direct' : 'is-indirect'
                            }`}
                          >
                            {row.mentionCategory}
                          </span>
                        </td>
                        <td className="table-cell is-mentions-count">
                          <div className={`table-count-badge ${row.mentionCount === 0 ? 'is-zero' : ''}`}>{row.mentionCount.toLocaleString()}</div>
                        </td>
                        <td className="table-cell is-action">
                          {row.modalItem ? (
                            <button
                              type="button"
                              className="button is-tiny is-white has-inner-shadow w-button"
                              onClick={() => setSelectedPromptModalItem(row.modalItem)}
                              style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                            >
                              View answer
                            </button>
                          ) : (
                            <div className="text-size-small text-colour-light-grey">—</div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
                {hasPromptPagination && (
                  <tr className="table-row is-pagination-row">
                    <td className="table-cell is-pagination" colSpan={6}>
                      <div className="pagination">
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setPromptPage((page) => Math.max(1, page - 1))}
                          disabled={promptPage === 1}
                        >
                          Previous
                        </button>
                        <div className="text-size-small text-weight-medium text-colour-alt-grey">
                          Page {promptPage} of {totalPromptPages}
                        </div>
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setPromptPage((page) => Math.min(totalPromptPages, page + 1))}
                          disabled={promptPage === totalPromptPages}
                        >
                          Next
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : viewMode === 'brands' ? (
            <table key="mentions-brands-table" className={`table-component mentions-table mentions-table--brands ${hasBrandPagination ? 'has-pagination' : ''}`}>
              <thead className="table-header">
                <tr className="table-row is-header-row">
                  <th className="table-head is-brand">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brand</div>
                  </th>
                  <th className="table-head is-mentions-count">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Total mentions</div>
                  </th>
                  <th className="table-head is-prompt-usage">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey mentions-table-head-label">
                      <span>Searches</span>
                      <MentionsHeaderPortalTooltip text="How many unique prompt searches mentioned this brand in the selected date range." />
                    </div>
                  </th>
                  <th className="table-head is-avg-position">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey mentions-table-head-label">
                      <span>Average ranking</span>
                      <MentionsHeaderPortalTooltip text="Average ranking position for this brand when it appears in tracked prompt responses." />
                    </div>
                  </th>
                  <th className="table-head is-cited-links">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey mentions-table-head-label">
                      <span>Linked sources</span>
                      <MentionsHeaderPortalTooltip text="How many cited source URLs were associated with this brand in the selected date range." />
                    </div>
                  </th>
                  <th className="table-head is-action">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Action</div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {brandRows.length === 0 ? (
                  <tr className="table-row">
                    <td className="table-cell" colSpan={6} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                      <div className="text-size-small text-colour-light-grey">
                        {gapOnly
                          ? 'No gap opportunities found for this filter yet.'
                          : `No brand mentions found for the ${selectedBrandEmptyCopy} yet.`}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedBrandRows.map((row) => (
                    <tr key={row.domain} className="table-row">
                      <td className="table-cell is-brand">
                        <div className="mention-source-cell">
                          <div
                            className="mention-favicon"
                            style={{ backgroundImage: `url(${row.faviconUrl})` }}
                          />
                          <div>
                            <div className="text-size-small text-weight-medium">{row.name}</div>
                            <div className="text-size-small text-colour-alt-grey">{row.domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell is-mentions-count">
                        <div className={`table-count-badge ${row.mentions === 0 ? 'is-zero' : ''}`}>{row.mentions.toLocaleString()}</div>
                      </td>
                      <td className="table-cell is-prompt-usage">
                        <div className="text-size-small text-colour-alt-grey">{row.promptCount.toLocaleString()}</div>
                      </td>
                      <td className="table-cell is-avg-position">
                        <div className="text-size-small text-colour-alt-grey">
                          {row.avgPosition ? row.avgPosition.toFixed(1) : '—'}
                        </div>
                      </td>
                      <td className="table-cell is-cited-links">
                        <div className="text-size-small text-colour-alt-grey">{row.citedLinks.toLocaleString()}</div>
                      </td>
                      <td className="table-cell is-action">
                        <button
                          type="button"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          onClick={() => {
                            setSelectedBrandDomains([row.domain]);
                            setViewMode('prompts');
                          }}
                          style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                        >
                          View Prompts
                        </button>
                      </td>
                    </tr>
                  ))
                )}
                {hasBrandPagination && (
                  <tr className="table-row is-pagination-row">
                    <td className="table-cell is-pagination" colSpan={6}>
                      <div className="pagination">
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setBrandPage((page) => Math.max(1, page - 1))}
                          disabled={brandPage === 1}
                        >
                          Previous
                        </button>
                        <div className="text-size-small text-weight-medium text-colour-alt-grey">
                          Page {brandPage} of {totalBrandPages}
                        </div>
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setBrandPage((page) => Math.min(totalBrandPages, page + 1))}
                          disabled={brandPage === totalBrandPages}
                        >
                          Next
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table key="mentions-links-table" className={`table-component mentions-table mentions-table--links ${hasCitationPagination ? 'has-pagination' : ''}`}>
              <thead className="table-header">
                <tr className="table-row is-header-row">
                  <th className="table-head is-source">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Source URL</div>
                  </th>
                  <th className="table-head is-category">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Category</div>
                  </th>
                  <th className="table-head is-source-type">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Source Type</div>
                  </th>
                  <th className="table-head is-prompt-text">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Prompt</div>
                  </th>
                  <th className="table-head is-brands">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Brands Mentioned</div>
                  </th>
                  <th className="table-head is-action">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Action</div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {mentionRows.length === 0 ? (
                  <tr className="table-row">
                    <td className="table-cell" colSpan={6} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                      <div className="text-size-small text-colour-light-grey">
                        {gapOnly
                          ? `No gap source opportunities found for the ${selectedBrandEmptyCopy} yet.`
                          : `No linked sources found for the ${selectedBrandEmptyCopy} yet. This can happen when a brand was mentioned in an answer but no linked source was captured for that mention.`}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedMentionRows.map((row) => (
                    <tr key={`${row.url}-${row.count}`} className="table-row">
                      <td className="table-cell is-source">
                        <div className="mention-source-cell">
                          <div
                            className="mention-favicon"
                            style={{ backgroundImage: `url(${row.faviconUrl})` }}
                          />
                          <div className="mentions-citation-source-meta">
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              className="mention-link-anchor text-size-xsmall text-weight-medium mentions-citation-title"
                            >
                              {row.title}
                            </a>
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              className="mentions-citation-url-pill text-size-tiny text-colour-alt-grey"
                            >
                              {row.url}
                            </a>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell is-category">
                        <span
                          className={`mentions-source-tag ${
                            row.mentionCategory === 'Direct' ? 'is-direct' : 'is-indirect'
                          }`}
                        >
                          {row.mentionCategory}
                        </span>
                      </td>
                      <td className="table-cell is-source-type">
                        <span
                          className={`mentions-source-tag ${
                            row.sourceType === 'Direct Brand'
                              ? 'is-direct'
                            : row.sourceType === 'Social'
                                ? 'is-social'
                                : row.sourceType === 'Government'
                                  ? 'is-government'
                                  : row.sourceType === 'News'
                                    ? 'is-news'
                                    : 'is-tech'
                          }`}
                        >
                          {row.sourceType}
                        </span>
                      </td>
                      <td className="table-cell is-prompt-text">
                        <MentionsPortalTextTooltip
                          text={row.promptText}
                          triggerClassName="mentions-citation-prompt-preview-trigger"
                          tooltipClassName="mentions-citation-prompt-portal-tooltip"
                          preferredPlacement="bottom"
                          horizontalAlign="left"
                        >
                          <div className="mentions-citation-prompt-preview-wrap">
                            <div className="text-size-xsmall text-weight-medium text-colour-dark-brown mentions-citation-prompt-preview">
                              {row.promptText}
                            </div>
                          </div>
                        </MentionsPortalTextTooltip>
                      </td>
                      <td className="table-cell is-brands">
                        {row.brandNames.length === 0 ? (
                          <div className="text-size-small text-colour-alt-grey">—</div>
                        ) : (
                          <div className="mentions-prompt-brand-list">
                            {row.brandNames.map((brandName, index) => {
                              const domain = row.brandDomains[index] || row.domain;
                              const matchedBrand = brandEntities.find((brand) => brand.domain === domain);
                              const faviconUrl =
                                matchedBrand?.faviconUrl ||
                                `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

                              return (
                                <div key={`${row.url}-${domain}-${brandName}`} className="mentions-prompt-brand-item">
                                  <div
                                    className="mention-favicon"
                                    style={{ backgroundImage: `url(${faviconUrl})` }}
                                  />
                                  <div className="text-size-tiny text-weight-medium text-colour-alt-grey">{brandName}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="table-cell is-action">
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                        >
                          Open URL
                        </a>
                      </td>
                    </tr>
                  ))
                )}
                {hasCitationPagination && (
                  <tr className="table-row is-pagination-row">
                    <td className="table-cell is-pagination" colSpan={6}>
                      <div className="pagination">
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setCitationPage((page) => Math.max(1, page - 1))}
                          disabled={citationPage === 1}
                        >
                          Previous
                        </button>
                        <div className="text-size-small text-weight-medium text-colour-alt-grey">
                          Page {citationPage} of {totalCitationPages}
                        </div>
                        <button
                          type="button"
                          className="button is-small is-white has-inner-shadow w-button"
                          onClick={() => setCitationPage((page) => Math.min(totalCitationPages, page + 1))}
                          disabled={citationPage === totalCitationPages}
                        >
                          Next
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          </div>
        </div>
      </div>

      <SearchResponseModal
        item={selectedPromptModalItem}
        onClose={() => setSelectedPromptModalItem(null)}
      />

      <div className="spacer-large"></div>
    </div>
  );
}
